import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Building2, Users, DollarSign, Edit2, Plus, Trash2, User, HelpCircle, ArrowLeft, TrendingUp, BarChart3, PieChart as PieChartIcon, TrendingDown, Settings, Check, FileText, X, ArrowUpDown, ArrowUp, ArrowDown, Calendar as CalendarIcon, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { format } from "date-fns";
import {
  useCommissionSplitProfiles,
  useCreateCommissionSplitProfile,
  useUpdateCommissionSplitProfile,
  useDeleteCommissionSplitProfile,
  useUpdateClientAccountManagerSplits,
  useSaveCommissionSplitMilestones,
} from "@/hooks/useCommissionSplitProfiles";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import { calculateEnrollmentPremium } from "@/lib/enrollmentCalculator";
// DEV-262: shared premium-data loader (one cached contributions download,
// bulk rates incl. buy-up base plans) behind this tab's local cache helpers
import { fetchPlanPremiumData } from "@/hooks/usePlanPremiumData";
import { CarrierCommissionStatementsTab } from "./CarrierCommissionStatementsTab";
import { useCommissionSummary } from "@/hooks/useCommissionSummary";
import { formatGrowthPct } from "@shared/utils/commissionSummary";
import { parseDateLocal, cn } from "@/lib/utils";

const getLocalYearMonthString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const getLocalDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const parseMonthDate = (monthString: string): Date => {
  const [year, month] = monthString.split("-").map(Number);
  return new Date(year, month - 1, 1);
};

// MonthPicker Component
function MonthPicker({ value, onChange, className }: { value: string; onChange: (value: string) => void; className?: string }) {
  const [open, setOpen] = useState(false);
  
  const currentDate = value ? parseMonthDate(value) : new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = value ? parseInt(value.split('-')[1]) - 1 : currentDate.getMonth();
  
  const [displayYear, setDisplayYear] = useState(currentYear);
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const handleMonthSelect = (monthIndex: number) => {
    const monthStr = String(monthIndex + 1).padStart(2, '0');
    onChange(`${displayYear}-${monthStr}`);
    setOpen(false);
  };
  
  const displayValue = value 
    ? format(parseMonthDate(value), 'MMMM yyyy')
    : 'Select month';
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayValue}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="start">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDisplayYear(displayYear - 1)}
            >
              ←
            </Button>
            <div className="font-semibold">{displayYear}</div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDisplayYear(displayYear + 1)}
            >
              →
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {months.map((month, index) => {
              const isSelected = displayYear === currentYear && index === currentMonth;
              return (
                <Button
                  key={month}
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  className="h-9"
                  onClick={() => handleMonthSelect(index)}
                >
                  {month.substring(0, 3)}
                </Button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Helper functions
const parseCommissionValue = (value: string): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/[$,]/g, '')) || 0;
};

const formatMonth = (monthString: string): string => {
  try {
    const date = parseMonthDate(monthString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch {
    return 'N/A';
  }
};

type CommissionSortInfo = {
  parentId?: number;
  createdAt?: string;
  orderIndex?: number;
  isAdjustment?: boolean;
};

const getCommissionSortStorageKey = (accountingMonth: string) =>
  `commissionSortMap:${accountingMonth || "unknown"}`;

const readCommissionSortMap = (accountingMonth: string): Record<string, CommissionSortInfo> => {
  if (typeof window === 'undefined') return {};
  try {
    const key = getCommissionSortStorageKey(accountingMonth);
    return JSON.parse(sessionStorage.getItem(key) || '{}');
  } catch {
    return {};
  }
};

const writeCommissionSortMap = (accountingMonth: string, map: Record<string, CommissionSortInfo>) => {
  if (typeof window === 'undefined') return;
  try {
    const key = getCommissionSortStorageKey(accountingMonth);
    sessionStorage.setItem(key, JSON.stringify(map));
  } catch {
    // ignore
  }
};

const formatCurrency = (value: string) => {
  if (!value) return '-';
  const numericValue = parseFloat(value.replace(/[$,]/g, ''));
  if (isNaN(numericValue)) return '-';
  return `$${numericValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

const calculateCommission = (premium: number, rate: number): number => {
  return (premium * rate) / 100;
};

const toDateOnly = (value: any): Date | null => {
  if (value === undefined || value === null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  if (!Number.isFinite(time)) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const getMonthRange = (yyyyMm: string): { start: Date; end: Date } | null => {
  if (!yyyyMm) return null;
  const [year, month] = String(yyyyMm).split("-").map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return null;
  return { start, end };
};

const isActiveDuringRange = (args: {
  effectiveDate: any;
  endDate: any;
  rangeStart: Date;
  rangeEnd: Date;
}): boolean => {
  const effective = toDateOnly(args.effectiveDate);
  const end = toDateOnly(args.endDate);
  
  // Enrollment hasn't started yet during this range
  if (effective && effective > args.rangeEnd) return false;
  
  // Enrollment already ended before this range started
  if (end && end < args.rangeStart) return false;
  
  return true;
};

type CommissionCalculationType = "percentage" | "flat" | "pepm" | "pmpm";

const parseCommissionCalculationType = (value: any): CommissionCalculationType => {
  const v = String(value || "percentage").toLowerCase();
  if (v === "flat" || v === "pepm" || v === "pmpm" || v === "percentage") return v;
  return "percentage";
};

const parseMoney = (value: any): number => {
  if (value === undefined || value === null) return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const s = String(value);
  return parseFloat(s.replace(/[$,]/g, "")) || 0;
};

const isWaivedEnrollmentRow = (row: any): boolean => {
  const status = String(row?.status || "").toLowerCase();
  return Boolean(row?.waivedCoverage) || status === "waived";
};

const isAdjustmentCommissionRow = (row: any): boolean => {
  const projectedPremiumBlank = row?.projectedPremium === null || row?.projectedPremium === undefined || row?.projectedPremium === "";
  const projectedCommissionBlank = row?.projectedCommission === null || row?.projectedCommission === undefined || row?.projectedCommission === "";
  return projectedPremiumBlank && projectedCommissionBlank;
};

const calculateCommissionFromPlan = (args: {
  plan: any;
  // This client's own commission override (client_plans row), if any - takes
  // precedence over the plan's default so one client's negotiated rate never
  // has to be set as the plan's default (which would apply to every other
  // client on that plan too).
  clientPlan?: any;
  projectedPremium: number;
  enrolledEmployeeCount: number;
  enrolledMemberCount: number;
}) => {
  const calcType = parseCommissionCalculationType(
    args.clientPlan?.commissionCalculationType ?? args.plan?.commissionCalculationType,
  );
  const commissionRatePct = parseMoney(args.clientPlan?.commissionRate ?? args.plan?.commissionRate);
  const commissionValue = parseMoney(args.clientPlan?.commissionValue ?? args.plan?.commissionValue);

  if (calcType === "percentage") {
    return {
      commissionCalculationType: calcType,
      commissionRate: commissionRatePct,
      commissionValue: null as string | null,
      projectedCommission: calculateCommission(args.projectedPremium, commissionRatePct),
    };
  }

  if (calcType === "flat") {
    return {
      commissionCalculationType: calcType,
      commissionRate: 0,
      commissionValue: commissionValue.toString(),
      projectedCommission: commissionValue,
    };
  }

  if (calcType === "pepm") {
    return {
      commissionCalculationType: calcType,
      commissionRate: 0,
      commissionValue: commissionValue.toString(),
      projectedCommission: commissionValue * (args.enrolledEmployeeCount || 0),
    };
  }

  // pmpm
  return {
    commissionCalculationType: "pmpm" as const,
    commissionRate: 0,
    commissionValue: commissionValue.toString(),
    projectedCommission: commissionValue * (args.enrolledMemberCount || 0),
  };
};

const formatCommissionForRow = (row: any) => {
  const calcType = parseCommissionCalculationType(row?.commissionCalculationType);
  if (calcType === "percentage") {
    return row?.commissionRate
      ? `${parseFloat(row.commissionRate).toFixed(2)}%`
      : "N/A";
  }
  const v = parseMoney(row?.commissionValue);
  const dollars = `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (calcType === "flat") return `Flat ${dollars}`;
  if (calcType === "pepm") return `PEPM ${dollars}`;
  return `PMPM ${dollars}`;
};

const formatCommissionTypeLabel = (row: any) => {
  const calcType = parseCommissionCalculationType(row?.commissionCalculationType);
  if (calcType === "percentage") return "Percentage";
  if (calcType === "flat") return "Flat";
  if (calcType === "pepm") return "PEPM";
  return "PMPM";
};

const formatCommissionRateOrAmount = (row: any) => {
  const calcType = parseCommissionCalculationType(row?.commissionCalculationType);
  if (calcType === "percentage") {
    const pct = parseMoney(row?.commissionRate);
    return Number.isFinite(pct) ? `${pct.toFixed(2)}%` : "-";
  }

  const v = parseMoney(row?.commissionValue);
  return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export function CommissionManagementTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { canAny } = usePermissions();

  const canManageAgencyCommissions = canAny([
    "agency_commissions:manage",
  ]);

  const canGenerateAgencyStatements = canAny([
    "agency_commissions:reports",
    "agency_commissions:manage",
  ]);

  const trackingTabsListClass = canGenerateAgencyStatements
    ? "grid w-full grid-cols-6 bg-[#eff6fe] dark:bg-gray-800"
    : "grid w-full grid-cols-5 bg-[#eff6fe] dark:bg-gray-800";
  
  // Commission filters and state
  const [clientFilter, setClientFilter] = useState("all");
  const [carrierFilter, setCarrierFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [commissionSettingsOpen, setCommissionSettingsOpen] = useState(false);
  const [editSplitOpen, setEditSplitOpen] = useState(false);
  const [editSplitProfile, setEditSplitProfile] = useState<any>(null);
  const [addSplitProfileOpen, setAddSplitProfileOpen] = useState(false);
  const [clientAgentSplitAssignments, setClientAgentSplitAssignments] = useState<Record<number, number>>({});
  const [editClientSplitOpen, setEditClientSplitOpen] = useState(false);
  const [selectedClientForSplit, setSelectedClientForSplit] = useState<any>(null);
  const [addPrimarySecondarySplitOpen, setAddPrimarySecondarySplitOpen] = useState(false);
  const [producerAssignmentDialogOpen, setProducerAssignmentDialogOpen] = useState(false);
  const [producerAssignmentData, setProducerAssignmentData] = useState<{ clientId: number; userId: string; role: string; clientName: string } | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<{ clientId: number; userId: string; role: string; clientName: string; producerName: string; actualDelete?: boolean; managerId?: number } | null>(null);
  const [producerEffectiveDate, setProducerEffectiveDate] = useState(getLocalDateString());
  
  // Producer split assignment state (tracked by producer userId)
  const [producerExpandedState, setProducerExpandedState] = useState<Record<number, boolean>>({});
  const [producerSelectedSplits, setProducerSelectedSplits] = useState<Record<number, string>>({});
  
  // Client Assignments table sorting and search state
  const [assignmentSortColumn, setAssignmentSortColumn] = useState<'client' | 'primaryAgent' | 'secondaryAgent' | 'status' | null>('client');
  const [assignmentSortDirection, setAssignmentSortDirection] = useState<'asc' | 'desc'>('asc');
  const [assignmentSearchTerm, setAssignmentSearchTerm] = useState("");
  const [carrierPopoverOpen, setCarrierPopoverOpen] = useState(false);

  // Split profile form state
  const [splitFormName, setSplitFormName] = useState("");
  const [splitFormAgencyPct, setSplitFormAgencyPct] = useState("");
  const [splitFormAgentPct, setSplitFormAgentPct] = useState("");
  const [splitFormDescription, setSplitFormDescription] = useState("");
  const [splitFormIsDefault, setSplitFormIsDefault] = useState(false);
  const [splitFormIsMilestone, setSplitFormIsMilestone] = useState(false);
  const [milestoneRows, setMilestoneRows] = useState<Array<{ yearNumber: number; isYearAndBeyond: boolean; pct1: string; pct2: string }>>([
    { yearNumber: 1, isYearAndBeyond: false, pct1: "60", pct2: "40" },
    { yearNumber: 2, isYearAndBeyond: false, pct1: "50", pct2: "50" },
    { yearNumber: 3, isYearAndBeyond: true, pct1: "45", pct2: "55" },
  ]);
  const [selectedClientForNewLine, setSelectedClientForNewLine] = useState<number | null>(null);
  const [selectedPlanForNewLine, setSelectedPlanForNewLine] = useState<number | null>(null);
  const [selectedMonthForNewLine, setSelectedMonthForNewLine] = useState<string>("");
  const [newLineDialogOpen, setNewLineDialogOpen] = useState(false);
  const [isAutoPopulating, setIsAutoPopulating] = useState(false);
  const [currentCommissionMonth, setCurrentCommissionMonth] = useState(getLocalYearMonthString());
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [agentCommissionClientFilter, setAgentCommissionClientFilter] = useState("all");
  const [agentCommissionCarrierFilter, setAgentCommissionCarrierFilter] = useState("all");
  const [agentCommissionMonthFilter, setAgentCommissionMonthFilter] = useState("all");
  const [localInputValues, setLocalInputValues] = useState<Record<string, { actualPremium?: string; actualCommission?: string }>>({});
  const updateTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const lastAutoPopulateCheckRef = useRef<{ month: string; needed: boolean } | null>(null);

  // Small in-memory caches to avoid repeated API calls while the tab is open
  const dataCacheRef = useRef<{
    plansById: Map<number, any>;
    planRatesByKey: Map<string, any[]>;
    employeesByClientId: Map<number, any[]>;
    dependents: any[] | null;
    contributionsByPlanId: Map<number, any[]>;
    hasLoadedContributions: boolean;
  }>({
    plansById: new Map(),
    planRatesByKey: new Map(),
    employeesByClientId: new Map(),
    dependents: null,
    contributionsByPlanId: new Map(),
    hasLoadedContributions: false,
  });

  const fetchJson = useCallback(async (url: string) => {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Request failed (${res.status}) for ${url}`);
    }
    return res.json();
  }, []);

  const getDependents = useCallback(async () => {
    const cache = dataCacheRef.current;
    if (cache.dependents) return cache.dependents;
    try {
      const deps = await fetchJson(`/api/dependents`);
      cache.dependents = Array.isArray(deps) ? deps : [];
    } catch {
      cache.dependents = [];
    }
    return cache.dependents;
  }, [fetchJson]);

  const loadAllContributionsOnce = useCallback(async () => {
    const cache = dataCacheRef.current;
    if (cache.hasLoadedContributions) return;
    cache.hasLoadedContributions = true;
    try {
      // DEV-262: empty plan list = contributions-only load, shared app-wide
      // React Query cache instead of a direct fetch
      const { allPlanContributions } = await fetchPlanPremiumData([]);
      const list = Array.isArray(allPlanContributions) ? allPlanContributions : [];
      // Build a quick lookup map by planId
      list.forEach((c: any) => {
        if (!c?.planId) return;
        if (!cache.contributionsByPlanId.has(c.planId)) cache.contributionsByPlanId.set(c.planId, []);
        cache.contributionsByPlanId.get(c.planId)!.push(c);
      });
    } catch {
      // ignore; leave map empty
    }
  }, [fetchJson]);

  const getPlan = useCallback(async (planId: number) => {
    const cache = dataCacheRef.current;
    if (cache.plansById.has(planId)) return cache.plansById.get(planId);
    // Agency commissions users may not have `plans:view`. Use commission-scoped endpoint.
    const plan = await fetchJson(`/api/commissions/plans/${planId}`);
    cache.plansById.set(planId, plan);
    return plan;
  }, [fetchJson]);

  const getPlanRates = useCallback(async (planId: number, clientId: number, asOf?: string) => {
    const cache = dataCacheRef.current;
    // asOf is part of the cache key - a lookup for one month must not reuse
    // rates cached for a different month.
    const key = `${planId}:${clientId}:${asOf ?? ""}`;
    if (cache.planRatesByKey.has(key)) return cache.planRatesByKey.get(key)!;
    try {
      // DEV-262: bulk endpoint via the shared loader (was /api/plan-rates/:id)
      const { ratesByPlanId } = await fetchPlanPremiumData([planId], clientId, asOf);
      cache.planRatesByKey.set(key, ratesByPlanId[planId] || []);
    } catch {
      cache.planRatesByKey.set(key, []);
    }
    return cache.planRatesByKey.get(key)!;
  }, [fetchJson]);

  const getEmployeesForClient = useCallback(async (clientId: number) => {
    const cache = dataCacheRef.current;
    if (cache.employeesByClientId.has(clientId)) return cache.employeesByClientId.get(clientId)!;
    try {
      const emps = await fetchJson(`/api/employees?clientId=${clientId}`);
      cache.employeesByClientId.set(clientId, Array.isArray(emps) ? emps : []);
    } catch {
      cache.employeesByClientId.set(clientId, []);
    }
    return cache.employeesByClientId.get(clientId)!;
  }, [fetchJson]);

  const getPlanContributions = useCallback(async (planId: number) => {
    await loadAllContributionsOnce();
    const cache = dataCacheRef.current;
    return cache.contributionsByPlanId.get(planId) || [];
  }, [loadAllContributionsOnce]);

  // Fetch split profiles from API
  const { data: agencyAgentSplitProfiles = [], dataUpdatedAt: agencyAgentSplitsUpdatedAt } = useCommissionSplitProfiles("agency_agent");
  const { data: primarySecondarySplitProfiles = [], dataUpdatedAt: primarySecondarySplitsUpdatedAt } = useCommissionSplitProfiles("primary_secondary");
  
  const createSplitProfile = useCreateCommissionSplitProfile();
  const updateSplitProfile = useUpdateCommissionSplitProfile();
  const deleteSplitProfile = useDeleteCommissionSplitProfile();
  const updateClientSplits = useUpdateClientAccountManagerSplits();
  const saveMilestones = useSaveCommissionSplitMilestones();

  // Commission Data Queries
  const { data: commissions = [], isLoading: commissionsLoading } = useQuery<any[]>({
    queryKey: ["/api/commissions"],
  });

  const { data: clients = [], isLoading: clientsLoading, dataUpdatedAt: clientsUpdatedAt } = useQuery<any[]>({
    queryKey: ["/api/commissions/clients"],
  });

  const { data: carriers = [] } = useQuery<any[]>({
    queryKey: ["/api/commissions/carriers"],
  });

  // Invalidated by PlanOverview's coverage save (staleTime is Infinity).
  const { data: plansMissingCommission = [] } = useQuery<
    { clientPlanId: number; clientId: number; clientName: string; planId: number; planName: string }[]
  >({
    queryKey: ["/api/commissions/missing-commission-plans"],
  });

  const { data: profile } = useQuery({
    queryKey: ["/api/user/profile"],
    queryFn: async () => {
      const r = await fetch("/api/user/profile", { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load profile");
      return r.json();
    },
  });

  const agencyId = (profile?.agencyId || "").toString();

  const { data: orgJobTitles = [] } = useQuery<any[]>({
    queryKey: ["/api/org/job-titles", "agency", agencyId],
    enabled: !!agencyId,
    queryFn: async () => {
      const r = await fetch(`/api/org/job-titles?ownerType=agency&ownerId=${agencyId}`, {
        credentials: "include",
      });
      if (!r.ok) return [];
      const data = await r.json();
      return Array.isArray(data) ? data : [];
    },
  });

  const producerAgentJobTitleIds = useMemo(() => {
    const ids = new Set<number>();
    (orgJobTitles || []).forEach((jt: any) => {
      const name = String(jt?.name || "").trim().toLowerCase();
      const id = Number(jt?.id);
      if (!Number.isFinite(id)) return;
      if (name === "producer" || name === "agent") ids.add(id);
    });
    return ids;
  }, [orgJobTitles]);

  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ["/api/agency/employees"],
    queryFn: async () => {
      const r = await fetch("/api/agency/employees", { credentials: "include" });
      if (!r.ok) return [];
      const data = await r.json();
      return Array.isArray(data) ? data : [];
    },
  });

  const producerEmployees = useMemo(() => {
    if (producerAgentJobTitleIds.size === 0) return [];
    return (employees || []).filter((emp: any) => producerAgentJobTitleIds.has(Number(emp?.jobTitleId)));
  }, [employees, producerAgentJobTitleIds]);

  // Fetch all client account managers to build agent list
  // Include inactive agents so we can see their historical commissions
  const { data: allClientAccountManagers = [], refetch: refetchAccountManagers, dataUpdatedAt: accountManagersUpdatedAt } = useQuery<any[]>({
    queryKey: ["/api/client-account-managers"],
    queryFn: async ({ signal }) => {
      const res = await fetch(`/api/client-account-managers`, {
        signal,
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("Failed to fetch account managers");
      }
      const managers = await res.json();
      
      // Enrich with client names
      return managers.map((m: any) => {
        const client = clients.find((c: any) => c.id === m.clientId);
        return {
          ...m,
          clientName: client?.companyName || client?.name || "Unknown Client",
        };
      });
    },
    enabled: clients.length > 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  // DEV-328: totals, per-agent shares and growth come from one server summary
  // (the same one /my-commissions uses) instead of being recomputed here.
  const { data: commissionSummary, isLoading: summaryLoading } = useCommissionSummary(
    "agency",
    currentCommissionMonth,
    getLocalDateString(),
  );
  // Splits/assignments change the agent shares; refresh the summary when any of
  // them is refetched after the initial load.
  const summaryDepsRef = useRef<string | null>(null);
  useEffect(() => {
    const stamps = [clientsUpdatedAt, accountManagersUpdatedAt, agencyAgentSplitsUpdatedAt, primarySecondarySplitsUpdatedAt];
    if (stamps.some((t) => !t)) return;
    const signature = stamps.join(":");
    if (summaryDepsRef.current && summaryDepsRef.current !== signature) {
      queryClient.invalidateQueries({ queryKey: ["/api/commissions", "summary"] });
    }
    summaryDepsRef.current = signature;
  }, [queryClient, clientsUpdatedAt, accountManagersUpdatedAt, agencyAgentSplitsUpdatedAt, primarySecondarySplitsUpdatedAt]);

  const shareByRecordAndUser = useMemo(() => {
    const map = new Map<string, NonNullable<typeof commissionSummary>["shares"][number]>();
    (commissionSummary?.shares ?? []).forEach((s) => map.set(`${s.recordId}:${s.userId}`, s));
    return map;
  }, [commissionSummary]);
  const getRecordShare = (recordId: number, userId: string) => shareByRecordAndUser.get(`${recordId}:${userId}`);

  // Fetch all enrollments
  const { data: allEnrollments = [] } = useQuery<any[]>({
    queryKey: ["/api/enrollments"],
  });

  // Get client plans when a client is selected
  const { data: clientPlans = [] } = useQuery<any[]>({
    queryKey: ["/api/commissions/client", selectedClientForNewLine, "plans"],
    queryFn: () => {
      if (!selectedClientForNewLine) return Promise.resolve([]);
      return fetch(`/api/commissions/client/${selectedClientForNewLine}/plans`).then(res => res.json());
    },
    enabled: !!selectedClientForNewLine,
  });

  const allKnownMonths = useMemo(() => {
    const monthSet = new Set<string>();

    for (const record of commissions) {
      if (record?.accountingMonth) monthSet.add(String(record.accountingMonth));
      if (record?.month) monthSet.add(String(record.month));
    }

    if (currentCommissionMonth) monthSet.add(String(currentCommissionMonth));

    return Array.from(monthSet)
      .filter(Boolean)
      .sort((a, b) => b.localeCompare(a));
  }, [commissions, currentCommissionMonth]);

  // Filter commissions by accounting month
  // NOTE: We keep table ordering stable across edits/refetches by persisting
  // an `orderIndex` per record in sessionStorage.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const filtered = commissions.filter((c: any) => {
      if (c.accountingMonth) return c.accountingMonth === currentCommissionMonth;
      return c.month === currentCommissionMonth;
    });

    if (filtered.length === 0) return;

    const map = readCommissionSortMap(currentCommissionMonth);
    let changed = false;
    const existingMax = Object.values(map).reduce((max, info) => {
      const idx = Number(info?.orderIndex);
      return Number.isFinite(idx) && idx > max ? idx : max;
    }, -1);
    let next = existingMax + 1;

    // Assign an order index the first time we see a record for this accounting month.
    // This prevents rows from jumping when the backend returns a different sort after updates.
    for (const record of filtered) {
      const id = String(record?.id);
      if (!id) continue;
      const entry = map[id] || (map[id] = {});
      if (!Number.isFinite(Number(entry.orderIndex))) {
        entry.orderIndex = next;
        next += 1;
        changed = true;
      }
    }

    if (changed) {
      writeCommissionSortMap(currentCommissionMonth, map);
    }
  }, [commissions, currentCommissionMonth]);

  const currentMonthCommissions = useMemo(() => {
    const filtered = commissions.filter(c => {
      if (c.accountingMonth) {
        return c.accountingMonth === currentCommissionMonth;
      }
      return c.month === currentCommissionMonth;
    });
    
    if (filtered.length === 0) return [];

    // Sort to place duplicated records right after their parent
    const sortMap = readCommissionSortMap(currentCommissionMonth);
    
    // Use Maps for faster lookups instead of nested filters
    const childrenByParent = new Map<number, any[]>();
    const parents: any[] = [];
    
    filtered.forEach(record => {
      const sortInfo = sortMap[record.id];
      if (sortInfo?.parentId) {
        const parentId = sortInfo.parentId;
        if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
        childrenByParent.get(parentId)!.push(record);
      } else {
        parents.push(record);
      }
    });

    // Sort parents by stable orderIndex
    parents.sort((a, b) => {
      const aInfo = sortMap[a.id];
      const bInfo = sortMap[b.id];
      const aIdx = Number(aInfo?.orderIndex);
      const bIdx = Number(bInfo?.orderIndex);
      const aHas = Number.isFinite(aIdx);
      const bHas = Number.isFinite(bIdx);
      if (aHas && bHas) return aIdx - bIdx;
      if (aHas) return -1;
      if (bHas) return 1;
      return Number(a.id) - Number(b.id);
    });
    
    // Sort children by creation time
    childrenByParent.forEach(children => {
      children.sort((a, b) => {
        const aTime = new Date(sortMap[a.id]?.createdAt || 0).getTime();
        const bTime = new Date(sortMap[b.id]?.createdAt || 0).getTime();
        if (aTime !== bTime) return aTime - bTime;
        return Number(a.id) - Number(b.id);
      });
    });

    // Build result more efficiently with single pass
    const result: any[] = [];
    const visited = new Set<number>();

    const appendWithChildren = (record: any) => {
      const id = Number(record?.id);
      if (!Number.isFinite(id) || visited.has(id)) return;
      visited.add(id);
      result.push(record);

      const children = childrenByParent.get(id);
      if (children?.length) {
        children.forEach(appendWithChildren);
      }
    };

    parents.forEach(appendWithChildren);

    // Add orphans at the end
    const remaining = filtered.filter((r: any) => {
      const id = Number(r?.id);
      return Number.isFinite(id) && !visited.has(id);
    });

    remaining.sort((a: any, b: any) => {
      const aInfo = sortMap[a.id];
      const bInfo = sortMap[b.id];
      const aTime = new Date(aInfo?.createdAt || 0).getTime();
      const bTime = new Date(bInfo?.createdAt || 0).getTime();
      if (aTime !== bTime) return aTime - bTime;
      return Number(a.id) - Number(b.id);
    });

    remaining.forEach(appendWithChildren);
    return result;
  }, [commissions, currentCommissionMonth]);

  const getCommissionRootParentId = useCallback(
    (accountingMonth: string, recordId: number) => {
      const map = readCommissionSortMap(accountingMonth);
      const visited = new Set<number>();
      let current = Number(recordId);
      if (!Number.isFinite(current)) return recordId;

      while (true) {
        const info = map[String(current)];
        const parentId = Number(info?.parentId);
        if (!Number.isFinite(parentId) || parentId <= 0) return current;
        if (visited.has(parentId)) return parentId;
        visited.add(parentId);
        current = parentId;
      }
    },
    []
  );

  // Extract distinct carriers actually used in commission data, with name lookup fallback
  const usedCarriers = useMemo(() => {
    const seen = new Map<number, { id: number; name: string }>();
    for (const r of currentMonthCommissions as any[]) {
      if (r.carrierId && !seen.has(r.carrierId)) {
        const name = r.carrierName || carriers.find((c: any) => c.id === r.carrierId)?.name;
        if (name) seen.set(r.carrierId, { id: r.carrierId, name });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [currentMonthCommissions, carriers]);

  // Filter commissions once (avoid repeating the same filters in render multiple times)
  const filteredCurrentMonthCommissions = useMemo(() => {
    return currentMonthCommissions.filter((r: any) => {
      const clientMatch = clientFilter === "all" || r.clientId?.toString() === clientFilter;
      const carrierMatch = carrierFilter === "all" || r.carrierId?.toString() === carrierFilter;
      const statusMatch = statusFilter === "all" || r.status?.toLowerCase() === statusFilter;
      return clientMatch && carrierMatch && statusMatch;
    });
  }, [currentMonthCommissions, clientFilter, carrierFilter, statusFilter]);

  // Calculate client summary data - single pass through data
  const clientSummaryData = useMemo(() => {
    const clientTotals = new Map<string, {
      clientName: string;
      projectedTotal: number;
      actualTotal: number;
      recordCount: number;
    }>();

    currentMonthCommissions.forEach(record => {
      const clientId = String(record.clientId);
      const clientName = record.clientName || 'Unknown Client';
      
      if (!clientTotals.has(clientId)) {
        clientTotals.set(clientId, { clientName, projectedTotal: 0, actualTotal: 0, recordCount: 0 });
      }

      const entry = clientTotals.get(clientId)!;
      entry.projectedTotal += parseCommissionValue(record.projectedCommission);
      entry.actualTotal += parseCommissionValue(record.actualCommission);
      entry.recordCount += 1;
    });

    return Array.from(clientTotals.values())
      .map(client => {
        const variance = client.actualTotal - client.projectedTotal;
        const variancePercent = client.projectedTotal > 0 ? ((variance / client.projectedTotal) * 100) : 0;
        return {
          clientName: client.clientName,
          projectedTotal: client.projectedTotal,
          actualTotal: client.actualTotal,
          variance,
          variancePercent,
          hasPendingRecords: client.actualTotal === 0
        };
      })
      .sort((a, b) => b.projectedTotal - a.projectedTotal);
  }, [currentMonthCommissions]);

  // Helper functions for commission calculations
  // NOTE: Agency/Agent split is client-scoped (one split per client).
  const getClientAgencySplit = (clientId: number) => {
    const assignedSplitId = Number((clients as any[]).find((c: any) => c.id === clientId)?.agencyAgentSplitId);
    const splitProfile = agencyAgentSplitProfiles.find((p: any) => p.id === assignedSplitId) ||
                        agencyAgentSplitProfiles.find((p: any) => p.isDefault);

    if (!splitProfile) {
      return { agentPercentage: "50%", agencyPercentage: "50%", splitName: "Standard Split" };
    }

    return {
      agentPercentage: `${splitProfile.agentPercentage}%`,
      agencyPercentage: `${splitProfile.agencyPercentage}%`,
      splitName: splitProfile.name
    };
  };

  const getClientAgentSplit = (clientId: number) => {
    const assignedSplitId = clientAgentSplitAssignments[clientId];
    const splitProfile = primarySecondarySplitProfiles.find(p => p.id === assignedSplitId) || 
                        primarySecondarySplitProfiles.find(p => p.isDefault);
    
    if (!splitProfile) {
      return { primaryPercentage: 50, secondaryPercentage: 50, splitName: "Equal Split" };
    }
    
    return {
      primaryPercentage: splitProfile.primaryPercentage,
      secondaryPercentage: splitProfile.secondaryPercentage,
      splitName: splitProfile.name
    };
  };

  // Handle sorting for client assignments table
  const handleAssignmentSort = (column: 'client' | 'primaryAgent' | 'secondaryAgent' | 'status') => {
    if (assignmentSortColumn === column) {
      setAssignmentSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setAssignmentSortColumn(column);
      setAssignmentSortDirection('asc');
    }
  };

  const getSortIcon = (column: 'client' | 'primaryAgent' | 'secondaryAgent' | 'status') => {
    if (assignmentSortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 inline opacity-50" />;
    }
    return assignmentSortDirection === 'asc'
      ? <ArrowUp className="h-4 w-4 ml-1 inline" />
      : <ArrowDown className="h-4 w-4 ml-1 inline" />;
  };

  const calculateAgentCommissions = (totalCommission: number, clientId: number, isSecondary: boolean = false) => {
    const agentSplit = getClientAgencySplit(clientId);
    const agentPercentage = parseFloat(agentSplit.agentPercentage.replace('%', ''));
    const agentPortion = totalCommission * (agentPercentage / 100);

    const clientSplit = getClientAgentSplit(clientId);
    const splitPercentage = Number(isSecondary ? clientSplit.secondaryPercentage : clientSplit.primaryPercentage) || 0;
    return agentPortion * (splitPercentage / 100);
  };

  // Helper to check if assignment is currently active (as of today)
  const isAssignmentCurrentlyActive = useCallback((assignment: any): boolean => {
    if (!assignment) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const effectiveDate = assignment.effectiveDate ? parseDateLocal(assignment.effectiveDate) : null;
    const endDate = assignment.endDate ? parseDateLocal(assignment.endDate) : null;
    
    // Assignment hasn't started yet
    if (effectiveDate && effectiveDate > today) return false;
    
    // Assignment has already ended (inclusive)
    if (endDate && endDate <= today) return false;
    
    return true;
  }, []);

  const getClientAssignedProducersLabel = (clientId: number) => {
    // Only show currently ACTIVE producer assignments
    const producers = allClientAccountManagers.filter(
      (m: any) => m.clientId === clientId && m.role?.toLowerCase().includes("producer") && isAssignmentCurrentlyActive(m)
    );

    const primary = producers.find(
      (m: any) => m.role?.toLowerCase().includes("primary") && m.role?.toLowerCase().includes("producer")
    );
    const secondary = producers.find(
      (m: any) => m.role?.toLowerCase().includes("secondary") && m.role?.toLowerCase().includes("producer")
    );

    const displayNameFor = (manager: any) => {
      const employee = employees.find((emp: any) => emp.id?.toString() === manager?.userId);
      return employee ? `${employee.firstName} ${employee.lastName}` : (manager?.userId || "-");
    };

    const primaryName = primary ? displayNameFor(primary) : "-";
    const secondaryName = secondary ? displayNameFor(secondary) : "-";

    const extras = producers
      .filter((m: any) => m !== primary && m !== secondary)
      .map((m: any) => displayNameFor(m))
      .filter(Boolean);

    return { primaryName, secondaryName, extras };
  };

  // Group clients by producer (agents only) - only show currently ACTIVE assignments
  const getProducerGroups = useMemo(() => {
    const groups: Record<string, { name: string; userId: number; primaryClients: number[]; secondaryClients: number[]; otherClients: number[] }> = {};
    
    allClientAccountManagers.forEach((manager: any) => {
      if (!manager.role?.toLowerCase().includes("producer")) return;
      // Skip inactive assignments in split management view
      if (!isAssignmentCurrentlyActive(manager)) return;
      
      const userId = String(manager.userId);
      if (!groups[userId]) {
        const employee = employees.find((e: any) => e.id?.toString() === userId);
        groups[userId] = {
          name: employee ? `${employee.firstName} ${employee.lastName}` : `Agent ${manager.userId}`,
          userId: manager.userId,
          primaryClients: [],
          secondaryClients: [],
          otherClients: []
        };
      }
      
      if (manager.role?.toLowerCase().includes("primary")) {
        groups[userId].primaryClients.push(manager.clientId);
      } else if (manager.role?.toLowerCase().includes("secondary")) {
        groups[userId].secondaryClients.push(manager.clientId);
      } else {
        groups[userId].otherClients.push(manager.clientId);
      }
    });
    
    return Object.values(groups);
  }, [allClientAccountManagers, employees, isAssignmentCurrentlyActive]);

  const updateClientAgencySplitMutation = useMutation({
    mutationFn: async ({ clientId, agencyAgentSplitId }: { clientId: number; agencyAgentSplitId: number | null }) => {
      const res = await apiRequest(
        'PATCH',
        `/api/commissions/clients/${clientId}/agency-agent-split`,
        { agencyAgentSplitId }
      );
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/commissions/clients"] });
      toast({ title: "Split Updated", description: "Client agency/agent split updated.", variant: "success" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err?.message || "Failed to update split.", variant: "destructive" });
    }
  });

  const updateClientAgencySplit = async (clientId: number, splitProfileId: number) => {
    // Optimistically update the local client list so the UI responds immediately.
    queryClient.setQueryData(["/api/commissions/clients"], (prev: any) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((c: any) => (c.id === clientId ? { ...c, agencyAgentSplitId: splitProfileId } : c));
    });
    await updateClientAgencySplitMutation.mutateAsync({ clientId, agencyAgentSplitId: splitProfileId });
  };

  // Update client agent split profile assignment
  const updateClientAgentSplit = async (clientId: number, splitProfileId: number) => {
    // Update local state immediately for responsive UI
    setClientAgentSplitAssignments(prev => ({
      ...prev,
      [clientId]: splitProfileId
    }));

    // Find all account manager records for this client and update them
    const clientManagers = allClientAccountManagers.filter(
      (manager: any) => manager.clientId === clientId
    );

    try {
      // Update all account managers for this client with the new primary/secondary split
      await Promise.all(
        clientManagers.map((manager: any) =>
          updateClientSplits.mutateAsync({
            id: manager.id,
            primarySecondarySplitId: splitProfileId,
          })
        )
      );

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["/api/client-account-managers"] });

      const client = clients.find(c => c.id === clientId);
      toast({
        title: "Split Updated",
        description: `Primary/Secondary split updated for ${client?.name || 'client'}`,
        variant: "success"
      });
    } catch (error: any) {
      toast({
        title: "Error updating split",
        description: error.message,
        variant: "destructive",
      });
      // Revert local state on error
      setClientAgentSplitAssignments(prev => {
        const updated = { ...prev };
        delete updated[clientId];
        return updated;
      });
    }
  };

  const upsertClientRepresentativeMutation = useMutation({
    mutationFn: async ({
      clientId,
      userId,
      role,
      effectiveDate,
    }: {
      clientId: number;
      userId: string;
      role: string;
      effectiveDate?: string;
    }) => {
      const res = await apiRequest("POST", `/api/clients/${clientId}/account-managers`, { userId, role, effectiveDate });
      return res.json();
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["/api/client-account-managers"] });
      const previous = queryClient.getQueryData<any[]>(["/api/client-account-managers"]);

      // Keep the per-client key in sync so Client Profile updates immediately.
      const repsKey = [`/api/clients/${variables.clientId}/account-managers`];
      const previousReps = queryClient.getQueryData<any[]>(repsKey);

      const roleKey = (variables.role || "").toLowerCase();
      const shouldLooselyReplace = roleKey.includes("primary") || roleKey.includes("secondary");
      const clientName =
        clients.find((c: any) => Number(c.id) === Number(variables.clientId))?.companyName ||
        clients.find((c: any) => Number(c.id) === Number(variables.clientId))?.name ||
        "";

      queryClient.setQueryData<any[]>(["/api/client-account-managers"], (current) => {
        const list = Array.isArray(current) ? current : [];
        const withoutRole = list.filter((m: any) => {
          if (Number(m.clientId) !== Number(variables.clientId)) return true;
          const mRole = String(m.role || "").toLowerCase();
          if (shouldLooselyReplace) {
            return !(mRole.includes("producer") && mRole.includes(roleKey.includes("primary") ? "primary" : "secondary"));
          }
          return m.role !== variables.role;
        });

        const optimistic = {
          id: -Date.now(),
          clientId: variables.clientId,
          userId: variables.userId,
          role: variables.role,
          clientName,
        };

        return [...withoutRole, optimistic];
      });

      queryClient.setQueryData<any[]>(repsKey, (current) => {
        const list = Array.isArray(current) ? current : [];
        const withoutRole = list.filter((r: any) => String(r.role || "") !== String(variables.role));
        const optimistic = {
          id: -Date.now(),
          clientId: variables.clientId,
          userId: variables.userId,
          role: variables.role,
        };
        return [...withoutRole, optimistic];
      });

      return { previous, previousReps, repsKey };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/client-account-managers"] });
      toast({
        title: "Assignment Updated",
        description: "Client representative assignment updated.",
        variant: "success",
      });
    },
    onError: (err: any, _variables, context) => {
      if ((context as any)?.previous) {
        queryClient.setQueryData(["/api/client-account-managers"], (context as any).previous);
      }
      if ((context as any)?.previousReps !== undefined && (context as any)?.repsKey) {
        queryClient.setQueryData((context as any).repsKey, (context as any).previousReps);
      }
      toast({
        title: "Error",
        description: err?.message || "Failed to update client assignment.",
        variant: "destructive",
      });
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/client-account-managers"] });
      // Cross-page refresh (Client Profile + list pages)
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", variables.clientId] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", String(variables.clientId)] });
      // Per-client representatives list used by AgencyRepresentatives
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${variables.clientId}/account-managers`] });
      // Force immediate refetch
      refetchAccountManagers();
    },
  });

  const removeClientRepresentativeMutation = useMutation({
    mutationFn: async ({
      clientId,
      userId,
      role,
      actualDelete,
      managerId,
    }: {
      clientId: number;
      userId: string;
      role?: string;
      actualDelete?: boolean;
      managerId?: number;
    }) => {
      const params = new URLSearchParams();
      if (role) params.append("role", role);
      if (actualDelete) params.append("actualDelete", "true");
      if (managerId) params.append("managerId", String(managerId));
      const queryString = params.toString() ? `?${params.toString()}` : "";
      await apiRequest("DELETE", `/api/clients/${clientId}/account-managers/${userId}${queryString}`);
      return { success: true };
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["/api/client-account-managers"] });
      const previous = queryClient.getQueryData<any[]>(["/api/client-account-managers"]);

      const repsKey = [`/api/clients/${variables.clientId}/account-managers`];
      const previousReps = queryClient.getQueryData<any[]>(repsKey);
      
      // Set end_date instead of removing (we now use soft deletes)
      const endDate = getLocalDateString();
      
      queryClient.setQueryData<any[]>(["/api/client-account-managers"], (current) => {
        const list = Array.isArray(current) ? current : [];
        if (variables.actualDelete) {
          if (variables.managerId) {
            return list.filter((m: any) => Number(m.id) !== Number(variables.managerId));
          }
          return list.filter((m: any) => !(
            Number(m.clientId) === Number(variables.clientId) &&
            String(m.userId) === String(variables.userId) &&
            (!variables.role || String(m.role || "") === String(variables.role))
          ));
        }
        return list.map((m: any) => {
          // Check if this is the record to end (override any existing endDate)
          const isMatch = 
            Number(m.clientId) === Number(variables.clientId) &&
            String(m.userId) === String(variables.userId) &&
            (!variables.role || String(m.role || "") === String(variables.role));
          
          if (isMatch) {
            return { ...m, endDate };
          }
          return m;
        });
      });

      queryClient.setQueryData<any[]>(repsKey, (current) => {
        const list = Array.isArray(current) ? current : [];
        if (variables.actualDelete) {
          if (variables.managerId) {
            return list.filter((r: any) => Number(r.id) !== Number(variables.managerId));
          }
          return list.filter((r: any) => !(
            String(r.userId) === String(variables.userId) &&
            (!variables.role || String(r.role || "") === String(variables.role))
          ));
        }
        return list.map((r: any) => {
          const isMatch = 
            String(r.userId) === String(variables.userId) &&
            (!variables.role || String(r.role || "") === String(variables.role));
          
          if (isMatch) {
            return { ...r, endDate };
          }
          return r;
        });
      });
      return { previous, previousReps, repsKey };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/client-account-managers"] });
      toast({
        title: "Assignment Removed",
        description: "Client representative removed.",
        variant: "success",
      });
    },
    onError: (err: any, _variables, context) => {
      if ((context as any)?.previous) {
        queryClient.setQueryData(["/api/client-account-managers"], (context as any).previous);
      }
      if ((context as any)?.previousReps !== undefined && (context as any)?.repsKey) {
        queryClient.setQueryData((context as any).repsKey, (context as any).previousReps);
      }
      toast({
        title: "Error",
        description: err?.message || "Failed to remove client assignment.",
        variant: "destructive",
      });
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/client-account-managers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", variables.clientId] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", String(variables.clientId)] });
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${variables.clientId}/account-managers`] });
      // Force immediate refetch
      refetchAccountManagers();
    },
  });

  // Get agent commission data (filtered by selected agent). Attribution (who
  // was the producer at the end of each record month) comes from the server
  // summary — the same shares /my-commissions shows that agent.
  const getAgentCommissionData = (userId: string) => {
    let records = commissions.filter((record) => !!getRecordShare(record.id, userId));

    // Apply filters
    if (agentCommissionClientFilter !== "all") {
      records = records.filter(r => r.clientId?.toString() === agentCommissionClientFilter);
    }
    if (agentCommissionCarrierFilter !== "all") {
      records = records.filter(r => r.carrierId?.toString() === agentCommissionCarrierFilter);
    }
    if (agentCommissionMonthFilter !== "all") {
      records = records.filter(r => (r.accountingMonth || r.month) === agentCommissionMonthFilter);
    }

    return records;
  };

  // Get months where the selected agent earned commissions
  const getAgentMonths = (userId: string) => {
    const monthSet = new Set<string>();
    (commissionSummary?.shares ?? []).forEach((s) => {
      if (s.userId === userId) monthSet.add(s.month);
    });

    // Sort in descending order (most recent first)
    return Array.from(monthSet).sort((a, b) => b.localeCompare(a));
  };

  // Get filtered agent total commissions (only from periods when agent was assigned)
  const getFilteredAgentTotalCommissions = (userId: string) => {
    return getAgentCommissionData(userId).reduce(
      (sum, record) => sum + (getRecordShare(record.id, userId)?.share ?? 0),
      0,
    );
  };

  // Commission CRUD mutations
  const createCommissionMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('POST', '/api/commissions', data);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/commissions'] });
      toast({ title: "Commission Created", description: "New commission line has been created.", variant: "success" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create commission line.", variant: "destructive" });
    }
  });

  const updateCommissionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => apiRequest('PATCH', `/api/commissions/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/commissions'] });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update commission.", variant: "destructive" });
    }
  });

  const deleteCommissionMutation = useMutation({
    mutationFn: async (id: number) => apiRequest('DELETE', `/api/commissions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/commissions'] });
      toast({ title: "Commission Deleted", description: "Commission line has been removed.", variant: "success" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete commission.", variant: "destructive" });
    }
  });

  // Handle creating a new commission line
  const handleCreateCommissionLine = async () => {
    if (!selectedClientForNewLine || !selectedPlanForNewLine || !selectedMonthForNewLine) {
      toast({ title: "Error", description: "Please select client, plan, and month", variant: "destructive" });
      return;
    }

    const selectedPlan = clientPlans.find((p: any) => p.planId === selectedPlanForNewLine);
    if (!selectedPlan) {
      toast({ title: "Error", description: "Selected plan not found", variant: "destructive" });
      return;
    }

    try {
      // Calculate projected premium based on enrollments for the selected month
      const range = getMonthRange(selectedMonthForNewLine);
      if (!range) {
        toast({ title: "Error", description: "Invalid month selected", variant: "destructive" });
        return;
      }
      const monthStartDate = range.start;
      const monthEndDate = range.end;
      
      // Filter enrollments for this client, plan, and that were active during the selected month
      const relevantEnrollments = allEnrollments.filter((e: any) => {
        if (e.clientId !== selectedClientForNewLine || e.planId !== selectedPlanForNewLine) return false;

        return isActiveDuringRange({
          effectiveDate: e.effectiveDate,
          endDate: e.endDate,
          rangeStart: monthStartDate,
          rangeEnd: monthEndDate,
        });
      });

      const [plan, planRates, planContributions, clientEmployees, dependents] = await Promise.all([
        getPlan(selectedPlanForNewLine),
        // Anchor on the selected month - omitting asOf defaults to today's
        // rate version regardless of which month this line is for.
        getPlanRates(selectedPlanForNewLine, selectedClientForNewLine, `${selectedMonthForNewLine}-01`),
        getPlanContributions(selectedPlanForNewLine),
        getEmployeesForClient(selectedClientForNewLine),
        getDependents(),
      ]);

      const calcType = parseCommissionCalculationType(plan?.commissionCalculationType);

      // Calculate totals by calculating each BASE enrollment's premium dynamically
      let projectedPremium = 0;
      const depsByEmployeeId = new Map<number, any[]>();
      (dependents || []).forEach((d: any) => {
        if (!depsByEmployeeId.has(d.employeeId)) depsByEmployeeId.set(d.employeeId, []);
        depsByEmployeeId.get(d.employeeId)!.push(d);
      });
      const activeNonWaived = relevantEnrollments.filter((e: any) => !isWaivedEnrollmentRow(e));
      const baseEnrollments = activeNonWaived.filter((e: any) => e?.dependentId == null);
      const enrolledEmployeeCount = new Set(baseEnrollments.map((e: any) => e.employeeId)).size;
      const enrolledMemberCount = activeNonWaived.length;

      for (const enrollment of baseEnrollments) {
        const employee = clientEmployees.find((e: any) => e.id === enrollment.employeeId);
        if (!employee) continue;

        const enrollmentDependents = depsByEmployeeId.get(enrollment.employeeId) || [];

        try {
          const result = calculateEnrollmentPremium({
            plan: plan,
            planRates: planRates,
            planContributions: planContributions,
            employee: employee,
            dependents: enrollmentDependents,
            coverageTier: enrollment.coverageTier,
          });
          
          projectedPremium += result.totalMonthlyRate || 0;
        } catch (error) {
          console.error('Error calculating premium for enrollment:', enrollment.id, error);
        }
      }

      const calc = calculateCommissionFromPlan({
        plan,
        clientPlan: selectedPlan,
        projectedPremium,
        enrolledEmployeeCount,
        enrolledMemberCount,
      });

      createCommissionMutation.mutate({
        clientId: selectedClientForNewLine,
        planId: selectedPlanForNewLine,
        carrierId: plan.carrierId,
        month: selectedMonthForNewLine,
        accountingMonth: currentCommissionMonth,
        projectedPremium: projectedPremium.toString(),
        commissionRate: calc.commissionRate.toString(),
        commissionCalculationType: calc.commissionCalculationType,
        commissionValue: calc.commissionValue,
        projectedCommission: calc.projectedCommission.toString(),
        premiumAmount: projectedPremium.toString(),
        commissionAmount: calc.projectedCommission.toString(),
        status: "pending",
      });

      setNewLineDialogOpen(false);
      setSelectedClientForNewLine(null);
      setSelectedPlanForNewLine(null);
      setSelectedMonthForNewLine("");
    } catch (error) {
      console.error('Error creating commission line:', error);
      toast({ 
        title: "Error", 
        description: "Failed to calculate premiums. Please try again.", 
        variant: "destructive" 
      });
    }
  };

  // Handle input changes with debouncing
  const handleInputChange = (recordId: number, field: string, value: string) => {
    setLocalInputValues(prev => ({
      ...prev,
      [recordId]: {
        ...prev[recordId],
        [field]: value
      }
    }));

    // Clear existing timer
    if (updateTimers.current[`${recordId}-${field}`]) {
      clearTimeout(updateTimers.current[`${recordId}-${field}`]);
    }

    // Set new timer for debounced update
    updateTimers.current[`${recordId}-${field}`] = setTimeout(() => {
      handleUpdateCommission(recordId, field, value);
    }, 500);
  };

  // Handle updating a commission record
  const handleUpdateCommission = async (id: number, field: string, value: string) => {
    const commission = commissions.find(c => c.id === id);
    if (!commission) return;

    // Skip update if value is empty for numeric fields
    if ((field === "actualPremium" || field === "actualCommission") && (!value || value.trim() === "")) {
      return;
    }

    const updates: any = { [field]: value };

    // Recalculate projected premium when month is changed
    if (field === "month" && value) {
      const accountingMonthKey = commission?.accountingMonth || currentCommissionMonth;
      const sortInfo = readCommissionSortMap(accountingMonthKey)[String(id)];
      const isAdjustment = Boolean(sortInfo?.isAdjustment) || isAdjustmentCommissionRow(commission);

      // Adjustments should NEVER pull/recompute projected values when the month changes.
      // Keep projected values blank regardless of what they currently are.
      if (isAdjustment) {
        updates.projectedPremium = null;
        updates.projectedCommission = null;
      } else {
      try {
        const range = getMonthRange(value);
        if (!range) {
          throw new Error("Invalid month");
        }
        const monthStartDate = range.start;
        const monthEndDate = range.end;
        
        // Filter enrollments for this client, plan, and that were active during the selected month
        const relevantEnrollments = allEnrollments.filter((e: any) => {
          if (e.clientId !== commission.clientId || e.planId !== commission.planId) return false;

          return isActiveDuringRange({
            effectiveDate: e.effectiveDate,
            endDate: e.endDate,
            rangeStart: monthStartDate,
            rangeEnd: monthEndDate,
          });
        });

        const [plan, planRates, planContributions, clientEmployees, dependents] = await Promise.all([
          getPlan(commission.planId),
          getPlanRates(commission.planId, commission.clientId, `${value}-01`),
          getPlanContributions(commission.planId),
          getEmployeesForClient(commission.clientId),
          getDependents(),
        ]);

        // Check if the plan itself is active during the selected month
        const isPlanActive = isActiveDuringRange({
          effectiveDate: plan.effectiveDate,
          endDate: plan.endDate,
          rangeStart: monthStartDate,
          rangeEnd: monthEndDate,
        });
        
        // If the plan is not active during this month, set projected values to 0
        if (!isPlanActive) {
          updates.projectedPremium = "0";
          updates.projectedCommission = "0";
        } else {
        // Calculate total projected premium (BASE enrollment rows only)
        let projectedPremium = 0;
        const depsByEmployeeId = new Map<number, any[]>();
        (dependents || []).forEach((d: any) => {
          if (!depsByEmployeeId.has(d.employeeId)) depsByEmployeeId.set(d.employeeId, []);
          depsByEmployeeId.get(d.employeeId)!.push(d);
        });
        const activeNonWaived = relevantEnrollments.filter((e: any) => !isWaivedEnrollmentRow(e));
        const baseEnrollments = activeNonWaived.filter((e: any) => e?.dependentId == null);
        const enrolledEmployeeCount = new Set(baseEnrollments.map((e: any) => e.employeeId)).size;
        const enrolledMemberCount = activeNonWaived.length;

        for (const enrollment of baseEnrollments) {
          const employee = clientEmployees.find((e: any) => e.id === enrollment.employeeId);
          if (!employee) continue;

          const enrollmentDependents = depsByEmployeeId.get(enrollment.employeeId) || [];

          try {
            const result = calculateEnrollmentPremium({
              plan: plan,
              planRates: planRates,
              planContributions: planContributions,
              employee: employee,
              dependents: enrollmentDependents,
              coverageTier: enrollment.coverageTier,
            });
            
            projectedPremium += result.totalMonthlyRate || 0;
          } catch (error) {
            console.error('Error calculating premium for enrollment:', enrollment.id, error);
          }
        }

        // Prefer calculation type/value saved on the commission row; fall back to the plan.
        const planForCalc = {
          ...plan,
          commissionCalculationType:
            commission?.commissionCalculationType ?? plan?.commissionCalculationType,
          commissionValue: commission?.commissionValue ?? plan?.commissionValue,
          commissionRate: commission?.commissionRate ?? plan?.commissionRate,
        };

        const calc = calculateCommissionFromPlan({
          plan: planForCalc,
          projectedPremium,
          enrolledEmployeeCount,
          enrolledMemberCount,
        });

        updates.projectedPremium = projectedPremium.toString();
        updates.projectedCommission = calc.projectedCommission.toString();
        updates.commissionRate = calc.commissionRate.toString();
        updates.commissionCalculationType = calc.commissionCalculationType;
        updates.commissionValue = calc.commissionValue;
        }
      } catch (error) {
        console.error('Error recalculating premium:', error);
        toast({ 
          title: "Warning", 
          description: "Could not recalculate premium. Using previous value.", 
          variant: "destructive" 
        });
      }
      }
    }

    // Update status based on completion
    const actualPremium = field === "actualPremium" ? value : commission.actualPremium;
    const actualCommission = field === "actualCommission" ? value : commission.actualCommission;

    // Status logic: complete only when BOTH actualPremium and actualCommission are filled
    if (actualPremium && actualCommission) {
      updates.status = "complete";
    } else if (actualPremium || actualCommission) {
      updates.status = "in_progress";
    } else {
      updates.status = "pending";
    }

    updateCommissionMutation.mutate({ id, data: updates });
  };

  // Handle deleting a commission record
  const handleDeleteCommission = (record: any) => {
    const id = Number(record?.id);
    if (!Number.isFinite(id)) return;

    // Remove only this row's sort metadata; do not touch children.
    const accountingMonthKey = record?.accountingMonth || currentCommissionMonth;
    const sortMap = readCommissionSortMap(accountingMonthKey);
    if (sortMap[String(id)]) {
      delete sortMap[String(id)];
      writeCommissionSortMap(accountingMonthKey, sortMap);
    }

    deleteCommissionMutation.mutate(id);
  };

  // Handle duplicating a commission record
  const handleDuplicateCommission = async (record: any) => {
    try {
      const accountingMonthForChild = record?.accountingMonth || currentCommissionMonth;
      const parentIdForChild = getCommissionRootParentId(accountingMonthForChild, record.id);
      const createdRecord = await createCommissionMutation.mutateAsync({
        clientId: record.clientId,
        planId: record.planId,
        carrierId: record.carrierId,
        month: "", // Empty - user will select the period month
        accountingMonth: accountingMonthForChild,
        projectedPremium: "0",
        commissionRate: record.commissionRate || "0",
        commissionCalculationType: record.commissionCalculationType,
        commissionValue: record.commissionValue ?? null,
        projectedCommission: "0",
        premiumAmount: "0", // Legacy field
        commissionAmount: "0", // Legacy field
        status: "pending",
      });
      
      // Store the parent-child relationship in sessionStorage for sorting
      if (createdRecord && createdRecord.id) {
        const sortMap = readCommissionSortMap(accountingMonthForChild);
        sortMap[createdRecord.id] = { parentId: parentIdForChild, createdAt: new Date().toISOString() };
        writeCommissionSortMap(accountingMonthForChild, sortMap);
      }
    } catch (error) {
      console.error('Error duplicating commission:', error);
    }
  };

  const handleAddAdjustmentCommission = async (record: any) => {
    try {
      const accountingMonthForChild = record?.accountingMonth || currentCommissionMonth;
      const parentIdForChild = getCommissionRootParentId(accountingMonthForChild, record.id);
      const createdRecord = await createCommissionMutation.mutateAsync({
        clientId: record.clientId,
        planId: record.planId,
        carrierId: record.carrierId,
        // For adjustments, default to the same commission period month as the parent.
        month: record.month || "",
        accountingMonth: accountingMonthForChild,
        // Keep projected values blank; user only enters actuals.
        projectedPremium: null,
        projectedCommission: null,
        premiumAmount: "0", // Legacy field
        commissionAmount: "0", // Legacy field
        // Keep rate/type fields for reporting consistency.
        commissionRate: record.commissionRate || "0",
        commissionCalculationType: record.commissionCalculationType,
        commissionValue: record.commissionValue ?? null,
        status: "pending",
      });

      if (createdRecord && createdRecord.id) {
        const sortMap = readCommissionSortMap(accountingMonthForChild);
        sortMap[createdRecord.id] = {
          parentId: parentIdForChild,
          createdAt: new Date().toISOString(),
          isAdjustment: true,
        };
        writeCommissionSortMap(accountingMonthForChild, sortMap);
      }
    } catch (error) {
      console.error('Error creating adjustment commission:', error);
      toast({
        title: "Error",
        description: "Failed to create adjustment line.",
        variant: "destructive",
      });
    }
  };

  // Auto-populate commission records for a new month
  const handleAutoPopulateCommissions = async (monthToPopulate: string) => {
    if (!monthToPopulate || isAutoPopulating) return;

    setIsAutoPopulating(true);

    try {
      let createdCount = 0;
      let skippedCount = 0;
      const recordsToCreate: any[] = [];

      // For each client, get their plans and prepare commission records
      for (const client of clients) {
        try {
          // Fetch client plans
          const plansRes = await fetch(`/api/client-plans?clientId=${client.id}`);
          if (!plansRes.ok) continue;
          
          const clientPlansData = await plansRes.json();
          if (!Array.isArray(clientPlansData) || clientPlansData.length === 0) continue;

          // For each plan, prepare a commission record
          for (const clientPlan of clientPlansData) {
            try {
              // Check if plan was active during or before the accounting month
              const range = getMonthRange(monthToPopulate);
              if (!range) continue;

              // Check if a record already exists for this client/plan/accounting month combination
              const existingRecord = commissions.find(c => 
                c.clientId === client.id && 
                c.planId === clientPlan.planId && 
                c.accountingMonth === monthToPopulate
              );

              if (existingRecord) {
                skippedCount++;
                continue;
              }

              // Fetch plan details and calculate projected values
              let plan: any;
              let planRates: any;
              let planContributions: any;
              let clientEmployees: any[];
              let dependents: any[];
              
              try {
                [plan, planRates, planContributions, clientEmployees, dependents] = await Promise.all([
                  getPlan(clientPlan.planId),
                  getPlanRates(clientPlan.planId, client.id, `${monthToPopulate}-01`),
                  getPlanContributions(clientPlan.planId),
                  getEmployeesForClient(client.id),
                  getDependents(),
                ]);
              } catch {
                continue;
              }

              // Now check if the plan itself is active during the accounting month
              const isPlanActiveInAccountingMonth = isActiveDuringRange({
                effectiveDate: plan.effectiveDate || clientPlan.effectiveDate,
                endDate: plan.endDate || clientPlan.endDate,
                rangeStart: range.start,
                rangeEnd: range.end,
              });
              if (!isPlanActiveInAccountingMonth) continue;

              // Calculate projected premium for this client/plan/month
              let projectedPremium = 0;
              const monthStartDate = range.start;
              const monthEndDate = range.end;
              
              // Filter enrollments for this client, plan, and active during the selected month
              const relevantEnrollments = allEnrollments.filter((e: any) => {
                if (e.clientId !== client.id || e.planId !== clientPlan.planId) return false;
                return isActiveDuringRange({
                  effectiveDate: e.effectiveDate,
                  endDate: e.endDate,
                  rangeStart: monthStartDate,
                  rangeEnd: monthEndDate,
                });
              });

              const depsByEmployeeId = new Map<number, any[]>();
              (dependents || []).forEach((d: any) => {
                if (!depsByEmployeeId.has(d.employeeId)) depsByEmployeeId.set(d.employeeId, []);
                depsByEmployeeId.get(d.employeeId)!.push(d);
              });

              const activeNonWaived = relevantEnrollments.filter((e: any) => !isWaivedEnrollmentRow(e));
              const baseEnrollments = activeNonWaived.filter((e: any) => e?.dependentId == null);
              const enrolledEmployeeCount = new Set(baseEnrollments.map((e: any) => e.employeeId)).size;
              const enrolledMemberCount = activeNonWaived.length;

              for (const enrollment of baseEnrollments) {
                const employee = clientEmployees.find((e: any) => e.id === enrollment.employeeId);
                if (!employee) continue;

                const enrollmentDependents = depsByEmployeeId.get(enrollment.employeeId) || [];

                try {
                  const result = calculateEnrollmentPremium({
                    plan: plan,
                    planRates: planRates,
                    planContributions: planContributions,
                    employee: employee,
                    dependents: enrollmentDependents,
                    coverageTier: enrollment.coverageTier,
                  });
                  projectedPremium += result.totalMonthlyRate || 0;
                } catch (error) {
                  console.error('Error calculating premium for enrollment:', enrollment.id, error);
                }
              }

              // Calculate commission
              const calc = calculateCommissionFromPlan({
                plan: plan,
                clientPlan,
                projectedPremium,
                enrolledEmployeeCount,
                enrolledMemberCount,
              });

              // Add to batch with calculated values
              recordsToCreate.push({
                clientId: client.id,
                planId: clientPlan.planId,
                carrierId: plan.carrierId,
                month: monthToPopulate,
                accountingMonth: monthToPopulate,
                projectedPremium: projectedPremium.toString(),
                commissionRate: calc.commissionRate.toString(),
                commissionCalculationType: calc.commissionCalculationType,
                commissionValue: calc.commissionValue,
                projectedCommission: calc.projectedCommission.toString(),
                premiumAmount: projectedPremium.toString(),
                commissionAmount: calc.projectedCommission.toString(),
                status: "pending",
              });
            } catch (error) {
              console.error(`Error preparing commission for client ${client.id}, plan ${clientPlan.planId}:`, error);
            }
          }
        } catch (error) {
          console.error(`Error processing client ${client.id}:`, error);
        }
      }

      // Create all records
      for (const record of recordsToCreate) {
        try {
          await apiRequest("POST", "/api/commissions", record);
          createdCount++;
        } catch (error) {
          console.error('Error creating commission record:', error);
        }
      }

      // Refresh the commission data
      queryClient.invalidateQueries({ queryKey: ["/api/commissions"] });
      
      if (createdCount > 0) {
        toast({ 
          title: "Commission Lines Ready", 
          description: `Created ${createdCount} commission lines with calculated projections. ${skippedCount > 0 ? `Skipped ${skippedCount} existing records.` : ''}`,
        });
      }
    } catch (error) {
      console.error('Error auto-populating commissions:', error);
      toast({ 
        title: "Error", 
        description: "Failed to auto-populate commission lines. Please try again.", 
        variant: "destructive" 
      });
    } finally {
      setIsAutoPopulating(false);
    }
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(updateTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  // Load split assignments from database
  useEffect(() => {
    if (allClientAccountManagers.length > 0) {
      const primarySecondaryAssignments: Record<number, number> = {};
      
      allClientAccountManagers.forEach((manager: any) => {
        // Set primary/secondary split for this client (use first assignment found)
        if (manager.primarySecondarySplitId && !primarySecondaryAssignments[manager.clientId]) {
          primarySecondaryAssignments[manager.clientId] = manager.primarySecondarySplitId;
        }
      });
      
      setClientAgentSplitAssignments(primarySecondaryAssignments);
    }
  }, [allClientAccountManagers]);

  // Auto-populate commission records for the selected accounting month.
  // Only auto-populate if NO records exist for this month yet.
  // Once records exist (even 1), assume user is curating them and don't auto-populate.
  // This works across different users/sessions since it checks the actual server state.
  useEffect(() => {
    if (!currentCommissionMonth || clients.length === 0 || commissionsLoading) return;
    
    // Skip if we already checked this month
    if (lastAutoPopulateCheckRef.current?.month === currentCommissionMonth) {
      return;
    }

    // Count actual records for this month
    const existingCount = commissions.filter(c => c.accountingMonth === currentCommissionMonth || c.month === currentCommissionMonth).length;

    // Only auto-populate if NO records exist yet
    const needsPopulation = existingCount === 0;
    lastAutoPopulateCheckRef.current = { month: currentCommissionMonth, needed: needsPopulation };

    if (needsPopulation) {
      handleAutoPopulateCommissions(currentCommissionMonth);
    }
  }, [currentCommissionMonth, clients.length, commissionsLoading, commissions.length]);

  // Aggregate agent data
  const agentData = useMemo(() => {
    const agentMap = new Map<string, {
      userId: string;
      name: string;
      email: string;
      role: string;
      clientIds: number[];
      clientNames: string[];
      totalCommissions: number;
      assignedClientCount: number;
      isActive: boolean;
    }>();

    // Build agent structures from account managers (include ALL agents, active and inactive)
    allClientAccountManagers.forEach((manager: any) => {
      if (!manager.role?.includes('producer')) return;
      
      const isActive = isAssignmentCurrentlyActive(manager); // Active if currently active based on today's date
      
      if (!agentMap.has(manager.userId)) {
        agentMap.set(manager.userId, {
          userId: manager.userId,
          name: manager.userId,
          email: '',
          role: manager.role || 'Producer',
          clientIds: [],
          clientNames: [],
          totalCommissions: 0,
          assignedClientCount: 0,
          isActive,
        });
      }
      
      const agent = agentMap.get(manager.userId)!;
      if (!agent.clientIds.includes(manager.clientId)) {
        agent.clientIds.push(manager.clientId);
      }
      if (manager.clientName && !agent.clientNames.includes(manager.clientName)) {
        agent.clientNames.push(manager.clientName);
      }
      
      // Keep agent marked as active if ANY assignment is active
      if (isActive && !agent.isActive) {
        agent.isActive = true;
      }
    });

    // DEV-328: totals and active client counts come from the server summary so
    // they match the agent's own /my-commissions page.
    (commissionSummary?.agents ?? []).forEach((summaryAgent) => {
      const agent = agentMap.get(summaryAgent.userId);
      if (!agent) return;
      agent.totalCommissions = summaryAgent.totalCommissions;
      agent.assignedClientCount = summaryAgent.assignedClientCount;
    });

    // Map to final structure with employee names and sort by active status, then commissions
    return Array.from(agentMap.values())
      .map(agent => {
        const employee = employees.find((emp: any) => emp.id?.toString() === agent.userId);
        return {
          ...agent,
          name: employee ? `${employee.firstName} ${employee.lastName}` : agent.userId,
          email: employee?.email || '',
        };
      })
      .sort((a, b) => {
        // Active agents first
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        // Then by total commissions
        return b.totalCommissions - a.totalCommissions;
      });
  }, [allClientAccountManagers, commissionSummary, employees, isAssignmentCurrentlyActive]);

  // Analytics computations
  const analyticsData = useMemo(() => {
    const allRecords = commissions || [];
    const totalYTD = allRecords.reduce((sum, record) => sum + parseCommissionValue(record.actualCommission || record.projectedCommission), 0);

    const carrierTotals: Record<string, { total: number; count: number }> = {};
    const coverageTotals: Record<string, number> = {};
    const monthlyTotals: Record<string, number> = {};

    allRecords.forEach(record => {
      const commission = parseCommissionValue(record.actualCommission || record.projectedCommission);
      const carrierName = record.carrierName || 'Unknown';
      const coverageType = (record.planType || 'Unknown').replace(' Insurance', '');
      const month = record.accountingMonth || record.month;

      if (!carrierTotals[carrierName]) carrierTotals[carrierName] = { total: 0, count: 0 };
      carrierTotals[carrierName].total += commission;
      carrierTotals[carrierName].count += 1;
      coverageTotals[coverageType] = (coverageTotals[coverageType] || 0) + commission;
      if (month) monthlyTotals[month] = (monthlyTotals[month] || 0) + commission;
    });

    const topCarrier = Object.entries(carrierTotals).sort((a, b) => b[1].total - a[1].total)[0]?.[0] || "N/A";
    const topCoverage = Object.entries(coverageTotals).sort((a, b) => b[1] - a[1])[0]?.[0] || "N/A";

    const sortedMonths = Object.keys(monthlyTotals).sort();
    const monthlyTrendData = sortedMonths.map((month, index) => {
      const commission = monthlyTotals[month];
      const prevCommission = index > 0 ? monthlyTotals[sortedMonths[index - 1]] : commission;
      const variance = prevCommission > 0 ? ((commission - prevCommission) / prevCommission) * 100 : 0;
      const [year, monthNum] = month.split('-').map(Number);
      const date = new Date(year, monthNum - 1, 1);
      return { month: date.toLocaleDateString('en-US', { month: 'short' }), commissions: Math.round(commission), variance: parseFloat(variance.toFixed(1)) };
    });

    const avgCarrierTotal = Object.values(carrierTotals).reduce((sum, d) => sum + d.total, 0) / (Object.keys(carrierTotals).length || 1);
    const carrierData = Object.entries(carrierTotals)
      .map(([carrier, data]) => ({
        carrier: carrier.replace(' Blue Cross', '').replace(' Dental', ''),
        amount: Math.round(data.total),
        variance: parseFloat((avgCarrierTotal > 0 ? ((data.total - avgCarrierTotal) / avgCarrierTotal) * 100 : 0).toFixed(1))
      }))
      .sort((a, b) => b.amount - a.amount).slice(0, 6);

    const totalCoverage = Object.values(coverageTotals).reduce((sum, v) => sum + v, 0);
    const coverageTypeData = Object.entries(coverageTotals)
      .map(([name, value]) => ({ name, value: Math.round(value), percentage: totalCoverage > 0 ? Math.round((value / totalCoverage) * 100) : 0 }))
      .sort((a, b) => b.value - a.value);

    // DEV-328: same-store total-over-total growth from the server summary
    // (averaging per-month % swings blew up on newly added book).
    const avgGrowth = commissionSummary?.avgMonthlyGrowthPct ?? null;

    // Detailed Variance Analysis - group by carrier and coverage type
    const varianceAnalysis: Array<{
      carrierId: number;
      carrierName: string;
      coverageType: string;
      currentMonth: number;
      lastMonth: number;
      momVariance: number;
      lastYear: number;
      yoyVariance: number;
    }> = [];

    // Get unique carrier/coverage combinations
    const carrierCoverageCombos = new Map<string, {
      carrierId: number;
      carrierName: string;
      coverageType: string;
      records: any[];
    }>();

    allRecords.forEach(record => {
      const key = `${record.carrierId}-${record.planType}`;
      if (!carrierCoverageCombos.has(key)) {
        carrierCoverageCombos.set(key, {
          carrierId: record.carrierId,
          carrierName: record.carrierName || 'Unknown',
          coverageType: (record.planType || 'Unknown').replace(' Insurance', ''),
          records: []
        });
      }
      carrierCoverageCombos.get(key)!.records.push(record);
    });

    // Calculate variance for each combination
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonthNum = today.getMonth();
    
    const currentMonthStr = `${currentYear}-${String(currentMonthNum + 1).padStart(2, '0')}`;
    const lastMonthDate = new Date(currentYear, currentMonthNum - 1, 1);
    const lastMonthStr = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;
    const lastYearDate = new Date(currentYear - 1, currentMonthNum, 1);
    const lastYearStr = `${lastYearDate.getFullYear()}-${String(lastYearDate.getMonth() + 1).padStart(2, '0')}`;

    carrierCoverageCombos.forEach(combo => {
      const currentMonthTotal = combo.records
        .filter(r => (r.accountingMonth || r.month) === currentMonthStr)
        .reduce((sum, r) => sum + parseCommissionValue(r.actualCommission || r.projectedCommission), 0);
      
      const lastMonthTotal = combo.records
        .filter(r => (r.accountingMonth || r.month) === lastMonthStr)
        .reduce((sum, r) => sum + parseCommissionValue(r.actualCommission || r.projectedCommission), 0);
      
      const lastYearTotal = combo.records
        .filter(r => (r.accountingMonth || r.month) === lastYearStr)
        .reduce((sum, r) => sum + parseCommissionValue(r.actualCommission || r.projectedCommission), 0);

      const momVariance = lastMonthTotal > 0 ? ((currentMonthTotal - lastMonthTotal) / lastMonthTotal) * 100 : 0;
      const yoyVariance = lastYearTotal > 0 ? ((currentMonthTotal - lastYearTotal) / lastYearTotal) * 100 : 0;

      if (currentMonthTotal > 0 || lastMonthTotal > 0 || lastYearTotal > 0) {
        varianceAnalysis.push({
          carrierId: combo.carrierId,
          carrierName: combo.carrierName,
          coverageType: combo.coverageType,
          currentMonth: currentMonthTotal,
          lastMonth: lastMonthTotal,
          momVariance,
          lastYear: lastYearTotal,
          yoyVariance
        });
      }
    });

    varianceAnalysis.sort((a, b) => b.currentMonth - a.currentMonth);

    return {
      totalYTD: Math.round(totalYTD),
      avgGrowth,
      topCarrier,
      topCarrierAmount: Math.round(carrierTotals[topCarrier]?.total || 0),
      topCoverage,
      topCoveragePercent: coverageTypeData[0]?.percentage || 0,
      monthlyTrendData,
      carrierData,
      coverageTypeData,
      varianceAnalysis
    };
  }, [commissions, commissionSummary]);


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Commission Management</h2>
      </div>

      {/* Commission Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Commissions</p>
                <p className="text-2xl font-bold" data-testid="agency-commission-total">
                  {summaryLoading ? (
                    <span className="text-muted-foreground">Loading...</span>
                  ) : (
                    formatCurrency((commissionSummary?.totalCommissions ?? 0).toFixed(2))
                  )}
                </p>
              </div>
              <DollarSign className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">This Month</p>
                <p className="text-2xl font-bold">
                  {summaryLoading ? (
                    <span className="text-muted-foreground">Loading...</span>
                  ) : (
                    formatCurrency((commissionSummary?.monthTotal ?? 0).toFixed(2))
                  )}
                </p>
              </div>
              <DollarSign className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Agents</p>
                <p className="text-2xl font-bold" data-testid="agency-active-agents">{commissionSummary?.activeAgentCount ?? 0}</p>
              </div>
              <Users className="h-6 w-6 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold" data-testid="agency-commission-pending">
                  {summaryLoading ? (
                    <span className="text-muted-foreground">Loading...</span>
                  ) : (
                    formatCurrency((commissionSummary?.pendingTotal ?? 0).toFixed(2))
                  )}
                </p>
                {/* Pending is for the selected accounting month, like the list below */}
                <p className="text-xs text-muted-foreground" data-testid="agency-commission-month-records">
                  {commissionSummary?.monthRecordCount ?? 0} records in{" "}
                  {parseMonthDate(currentCommissionMonth).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </p>
              </div>
              <DollarSign className="h-6 w-6 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {plansMissingCommission.length > 0 && (
        <Card className="border-amber-300 dark:border-amber-700" data-testid="plans-missing-commission">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Plans with no commission set</CardTitle>
            <CardDescription>
              Commissions won't calculate correctly for these plans until a commission amount is entered.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {Object.values(
              plansMissingCommission.reduce<Record<number, { clientName: string; plans: typeof plansMissingCommission }>>(
                (byClient, row) => {
                  (byClient[row.clientId] ??= { clientName: row.clientName, plans: [] }).plans.push(row);
                  return byClient;
                },
                {},
              ),
            ).map((group) => (
              <div key={group.plans[0].clientId}>
                <span className="font-medium">{group.clientName}:</span>{" "}
                {group.plans.map((row, index) => (
                  <React.Fragment key={row.clientPlanId}>
                    {index > 0 && ", "}
                    <a className="text-blue-600 hover:underline dark:text-blue-400" href={`/plans/${row.planId}?clientId=${row.clientId}`}>
                      {row.planName}
                    </a>
                  </React.Fragment>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Commission Tracking Tabs - Placeholder for now */}
      <Tabs defaultValue="accounting" className="space-y-6">
        <TabsList className={trackingTabsListClass}>
          <TabsTrigger value="accounting" className="bg-[#eff6fe] dark:bg-gray-700 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Accounting</TabsTrigger>
          <TabsTrigger value="agents" className="bg-[#eff6fe] dark:bg-gray-700 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Agents</TabsTrigger>
          <TabsTrigger value="split-management" className="bg-[#eff6fe] dark:bg-gray-700 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Split Management</TabsTrigger>
          <TabsTrigger value="assignments" className="bg-[#eff6fe] dark:bg-gray-700 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Client Assignments</TabsTrigger>
          {canGenerateAgencyStatements && (
            <TabsTrigger value="statements" className="bg-[#eff6fe] dark:bg-gray-700 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Statements</TabsTrigger>
          )}
          <TabsTrigger value="analytics" className="bg-[#eff6fe] dark:bg-gray-700 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Analytics</TabsTrigger>
        </TabsList>

        {canGenerateAgencyStatements && (
          <TabsContent value="statements" className="space-y-4">
            <CarrierCommissionStatementsTab statementMonth={currentCommissionMonth} />
          </TabsContent>
        )}

        <TabsContent value="accounting" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Client Commission Tracking
              </CardTitle>
              <div className="flex gap-4 mt-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Label htmlFor="commission-month-selector" className="text-sm font-medium">Accounting Month</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>
                            The month commissions are recorded and paid in. Rows are
                            grouped and totaled by this posting month — a record's
                            Coverage Month can differ (e.g. a February chargeback
                            posted and settled in March).
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <MonthPicker
                    value={currentCommissionMonth}
                    onChange={setCurrentCommissionMonth}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="flex gap-4 mt-4">
                <div className="flex-1">
                  <Label className="text-sm font-medium">Filter by Client</Label>
                  <Select value={clientFilter} onValueChange={setClientFilter}>
                    <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clients</SelectItem>
                      {[...clients].sort((a: any, b: any) => (a.name || "").localeCompare(b.name || "")).map((client: any) => (
                        <SelectItem key={client.id} value={client.id.toString()}>{client.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium">Filter by Carrier</Label>
                  <Popover open={carrierPopoverOpen} onOpenChange={setCarrierPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className={cn("w-full justify-between font-normal", carrierFilter === "all" && "text-muted-foreground")}
                      >
                        {carrierFilter === "all"
                          ? "All Carriers"
                          : usedCarriers.find((c) => c.id.toString() === carrierFilter)?.name || "Select carrier"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" onWheel={(e) => e.stopPropagation()}>
                      <Command>
                        <CommandInput placeholder="Search carriers..." />
                        <CommandEmpty>No carrier found.</CommandEmpty>
                        <CommandList style={{ maxHeight: "250px", overflowY: "auto" }}>
                          <CommandGroup>
                            <CommandItem
                              value="__all__"
                              onSelect={() => { setCarrierFilter("all"); setCarrierPopoverOpen(false); }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", carrierFilter === "all" ? "opacity-100" : "opacity-0")} />
                              All Carriers
                            </CommandItem>
                            {usedCarriers.map((carrier) => (
                              <CommandItem
                                key={carrier.id}
                                value={carrier.name}
                                onSelect={() => { setCarrierFilter(carrier.id.toString()); setCarrierPopoverOpen(false); }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", carrierFilter === carrier.id.toString() ? "opacity-100" : "opacity-0")} />
                                {carrier.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium">Filter by Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="complete">Complete</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" size="sm" onClick={() => { setClientFilter("all"); setCarrierFilter("all"); setStatusFilter("all"); }}>
                    Clear Filters
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing <span data-testid="agency-records-shown">{filteredCurrentMonthCommissions.length}</span> of{" "}
                    <span data-testid="agency-records-total">{currentMonthCommissions.length}</span> records for{" "}
                    <span className="font-medium">
                      {parseMonthDate(currentCommissionMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                  {isAutoPopulating && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="animate-pulse">Loading commission records...</span>
                    </div>
                  )}
                </div>
                {canManageAgencyCommissions && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAutoPopulateCommissions(currentCommissionMonth)}
                      disabled={isAutoPopulating}
                    >
                      {isAutoPopulating ? "Populating..." : "Auto-Populate"}
                    </Button>
                    <Dialog open={newLineDialogOpen} onOpenChange={setNewLineDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Add Commission Line
                        </Button>
                      </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Commission Line</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Select Client</Label>
                          <Select value={selectedClientForNewLine?.toString()} onValueChange={(value) => {
                            setSelectedClientForNewLine(parseInt(value));
                            setSelectedPlanForNewLine(null);
                          }}>
                            <SelectTrigger>
                              <SelectValue placeholder="Choose a client" />
                            </SelectTrigger>
                            <SelectContent>
                              {clients.map((client: any) => (
                                <SelectItem key={client.id} value={client.id.toString()}>
                                  {client.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {selectedClientForNewLine && (
                          <>
                            <div>
                              <Label>Select Plan</Label>
                              <Select value={selectedPlanForNewLine?.toString()} onValueChange={(value) => setSelectedPlanForNewLine(parseInt(value))}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Choose a plan" />
                                </SelectTrigger>
                                <SelectContent>
                                  {clientPlans.map((cp: any) => (
                                    <SelectItem key={cp.planId} value={cp.planId.toString()}>
                                      {cp.plan?.name} - {cp.plan?.carrier} ({cp.plan?.type})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Commission Period (Which Month Is This For?)</Label>
                              <p className="text-xs text-muted-foreground mb-2">The month the commission covers (e.g., select June if this is for June premiums, even if paid later)</p>
                              <MonthPicker
                                value={selectedMonthForNewLine}
                                onChange={setSelectedMonthForNewLine}
                                className="w-full"
                              />
                          </div>
                          {selectedPlanForNewLine && selectedMonthForNewLine && (
                            <Button onClick={handleCreateCommissionLine} className="w-full">
                              Create Commission Line
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto client-commission-tracking-table">
                {filteredCurrentMonthCommissions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No records match the selected filters.
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium">Client</th>
                        <th className="text-left px-4 py-3 text-sm font-medium">Carrier/Line</th>
                        <th className="text-left px-4 py-3 text-sm font-medium">Coverage Month</th>
                        <th className="text-left px-4 py-3 text-sm font-medium">Commission Type</th>
                        <th className="text-left px-4 py-3 text-sm font-medium">Rate/Amount</th>
                        <th className="text-left px-4 py-3 text-sm font-medium">Projected Premium</th>
                        <th className="text-left px-4 py-3 text-sm font-medium">Actual Premium</th>
                        <th className="text-left px-4 py-3 text-sm font-medium">Projected Commission</th>
                        <th className="text-left px-4 py-3 text-sm font-medium">Actual Commission</th>
                        <th className="text-left px-4 py-3 text-sm font-medium">Status</th>
                        <th className="text-left px-4 py-3 text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredCurrentMonthCommissions.map((record) => (
                        <tr key={record.id} className="hover:bg-muted/50">
                          <td className="px-4 py-3 text-sm font-medium">{record.clientName || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm">
                            <div>
                              <p className="font-medium">{record.carrierName || 'N/A'}</p>
                              <p className="text-xs text-muted-foreground">{record.planType || 'N/A'}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <MonthPicker
                              value={record.month || ''}
                              onChange={(value) => handleUpdateCommission(record.id, "month", value)}
                              className="h-8 w-40"
                            />
                            {/* A row can cover one month but be POSTED in another
                                (e.g. a February chargeback recorded in March). The
                                accounting view groups by posting month, so make the
                                divergence explicit instead of looking like a bug. */}
                            {(() => {
                              const posted = String(record.accountingMonth || currentCommissionMonth || "");
                              const coverage = String(record.month || "");
                              if (!posted || !coverage || posted === coverage) return null;
                              const [y, m] = posted.split("-").map(Number);
                              const postedLabel = y && m
                                ? new Date(y, m - 1).toLocaleDateString("en-US", { month: "long", year: "numeric" })
                                : posted;
                              return (
                                <p className="mt-1 text-xs text-muted-foreground">
                                  Posted in {postedLabel}
                                </p>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {formatCommissionTypeLabel(record)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">
                            {formatCommissionRateOrAmount(record)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {record.projectedPremium ? `$${parseFloat(record.projectedPremium).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                              <Input 
                                placeholder="0.00" 
                                className="h-8 w-32 pl-6"
                                type="number"
                                step="0.01"
                                min="0"
                                value={localInputValues[record.id]?.actualPremium ?? record.actualPremium ?? ''}
                                onChange={(e) => handleInputChange(record.id, "actualPremium", e.target.value)}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {record.projectedCommission ? `$${parseFloat(record.projectedCommission).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                              <Input 
                                placeholder="0.00" 
                                className="h-8 w-32 pl-6"
                                type="number"
                                step="0.01"
                                min="0"
                                value={localInputValues[record.id]?.actualCommission ?? record.actualCommission ?? ''}
                                onChange={(e) => handleInputChange(record.id, "actualCommission", e.target.value)}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={
                              record.status === "complete" 
                                ? "bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-200"
                                : record.status === "in_progress"
                                ? "bg-blue-100 text-blue-800 dark:bg-blue-950/20 dark:text-blue-200"
                                : "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/20 dark:text-yellow-200"
                            }>
                              {record.status === "in_progress" ? "In Progress" : record.status === "complete" ? "Complete" : "Pending"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    title="Add a new line or adjustment"
                                  >
                                    <Plus className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                  <DropdownMenuLabel>Add</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleDuplicateCommission(record)}>
                                    New line
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleAddAdjustmentCommission(record)}>
                                    Adjustment
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteCommission(record)}
                                title="Delete this line"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Commission Summary by Client */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Commission Summary by Client
              </CardTitle>
              <CardDescription>Summary for {parseMonthDate(currentCommissionMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</CardDescription>
            </CardHeader>
            <CardContent>
              {clientSummaryData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No client data available for this month.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {clientSummaryData.map((summary, index) => (
                    <Card key={index} className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <div className="space-y-2">
                          <h4 className="font-semibold text-sm">{summary.clientName}</h4>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <p className="text-muted-foreground">Projected</p>
                              <p className="font-medium">${summary.projectedTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Actual</p>
                              <p className="font-medium">{summary.hasPendingRecords ? 'Pending' : `$${summary.actualTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Variance</p>
                              <p className={`font-medium ${summary.hasPendingRecords ? 'text-muted-foreground' : summary.variance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {summary.hasPendingRecords ? 'TBD' : `${summary.variance >= 0 ? '+' : ''}$${Math.abs(summary.variance).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">%</p>
                              <p className={`font-medium ${summary.hasPendingRecords ? 'text-muted-foreground' : summary.variancePercent < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {summary.hasPendingRecords ? 'TBD' : `${summary.variancePercent >= 0 ? '+' : ''}${summary.variancePercent.toFixed(1)}%`}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="agents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Active Agents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedAgent ? (
                <div className="space-y-4">
                  {agentData.length > 0 ? agentData.map((agent, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors" onClick={() => setSelectedAgent(agent)}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${agent.isActive ? 'bg-blue-100 dark:bg-blue-900' : 'bg-gray-100 dark:bg-gray-800'}`}>
                          <User className={`h-5 w-5 ${agent.isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-600'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className={`font-medium ${!agent.isActive ? 'text-muted-foreground' : ''}`}>{agent.name}</p>
                            {!agent.isActive && (
                              <Badge variant="secondary" className="text-xs">Inactive</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{agent.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{agent.assignedClientCount} clients</p>
                        <p className="text-sm text-muted-foreground">{formatCurrency(agent.totalCommissions.toString())} earned</p>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 text-muted-foreground">
                      No agents assigned to clients yet. Assign agents through Agency Representatives in the client details.
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-6">
                    <Button variant="outline" size="sm" onClick={() => setSelectedAgent(null)}>
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Agents
                    </Button>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">{selectedAgent.name}</h3>
                        <p className="text-sm text-muted-foreground">{selectedAgent.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="text-sm font-medium">Assigned Clients</p>
                            <p className="text-lg font-bold" data-testid="agency-agent-assigned-clients">{selectedAgent.assignedClientCount}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-5 w-5 text-green-600" />
                          <div>
                            <p className="text-sm font-medium">Total Commissions</p>
                            <p className="text-lg font-bold" data-testid="agency-agent-total">{formatCurrency(getFilteredAgentTotalCommissions(selectedAgent.userId).toFixed(2))}</p>
                            {agentCommissionMonthFilter !== "all" && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Filtered by month
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-purple-600" />
                          <div>
                            <p className="text-sm font-medium">Status</p>
                            <Badge variant={selectedAgent.isActive ? "default" : "secondary"} className={selectedAgent.isActive ? "bg-green-600" : ""}>
                              {selectedAgent.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  {/* Commission Details Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Commission Details</CardTitle>
                      <CardDescription>
                        Detailed commission breakdown for {selectedAgent.name}'s clients
                      </CardDescription>
                      <div className="flex gap-4 mt-4">
                        <div className="flex-1">
                          <Label className="text-sm font-medium">Filter by Client</Label>
                          <Select value={agentCommissionClientFilter} onValueChange={setAgentCommissionClientFilter}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select client" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Clients</SelectItem>
                              {Array.from(selectedAgent.clientIds).map((clientId: any) => {
                                const client = clients.find(c => c.id === clientId);
                                return client ? (
                                  <SelectItem key={clientId} value={clientId.toString()}>
                                    {client.name}
                                  </SelectItem>
                                ) : null;
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex-1">
                          <Label className="text-sm font-medium">Filter by Carrier</Label>
                          <Select value={agentCommissionCarrierFilter} onValueChange={setAgentCommissionCarrierFilter}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select carrier" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Carriers</SelectItem>
                              {(() => {
                                const agentCommissions = commissions.filter(record => 
                                  selectedAgent.clientIds.includes(record.clientId)
                                );
                                const uniqueCarrierIds = Array.from(new Set(agentCommissions.map(c => c.carrierId)));
                                return uniqueCarrierIds.map(carrierId => {
                                  const carrier = carriers.find((c: any) => c.id === carrierId);
                                  return carrier ? (
                                    <SelectItem key={carrier.id} value={carrier.id.toString()}>
                                      {carrier.name}
                                    </SelectItem>
                                  ) : null;
                                });
                              })()}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex-1">
                          <Label className="text-sm font-medium">Filter by Month (Commission)</Label>
                          <Select value={agentCommissionMonthFilter} onValueChange={setAgentCommissionMonthFilter}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select month" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Months</SelectItem>
                              {getAgentMonths(selectedAgent.userId).map((month) => (
                                <SelectItem key={month} value={month}>
                                  {formatMonth(month)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setAgentCommissionClientFilter("all");
                              setAgentCommissionCarrierFilter("all");
                              setAgentCommissionMonthFilter("all");
                            }}
                          >
                            Clear Filters
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const records = getAgentCommissionData(selectedAgent.userId);
                        const totalAgentAmount = getFilteredAgentTotalCommissions(selectedAgent.userId);

                        return (
                          <>
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-muted/50 border-b">
                                  <tr>
                                    <th className="text-left px-4 py-3 text-sm font-medium">Client</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium">Carrier/Line</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium">Commission Month</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium">Period Month</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium">Actual Premium</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium">Actual Commission</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium">Agent Split</th>
                                    <th className="text-left px-4 py-3 text-sm font-medium">Agent Amount</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {records.map((record) => {
                                    const share = getRecordShare(record.id, selectedAgent.userId);
                                    const agentAmount = share?.share ?? 0;
                                    const hasMultipleAgents = share?.splitPct != null;
                                    const isSecondary = !!share?.isSecondary;
                                    const agentSplitDisplay = share?.splitPct ?? 100;

                                    return (
                                      <tr key={record.id} className="hover:bg-muted/50">
                                        <td className="px-4 py-3 text-sm font-medium">{record.clientName}</td>
                                        <td className="px-4 py-3 text-sm">
                                          <div>
                                            <p className="font-medium">{record.carrierName}</p>
                                            <p className="text-xs text-muted-foreground">{record.planType}</p>
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm">{record.accountingMonth ? formatMonth(record.accountingMonth) : '-'}</td>
                                        <td className="px-4 py-3 text-sm">{record.month ? formatMonth(record.month) : '-'}</td>
                                        <td className="px-4 py-3 text-sm">{formatCurrency(record.actualPremium)}</td>
                                        <td className="px-4 py-3 text-sm">{formatCurrency(record.actualCommission)}</td>
                                        <td className="px-4 py-3 text-sm">
                                          <div>
                                            <p className="font-medium">{share ? `${share.agentPct}%` : '-'}</p>
                                            {hasMultipleAgents && (
                                              <p className="text-xs text-muted-foreground">
                                                × {agentSplitDisplay}% ({isSecondary ? 'Secondary' : 'Primary'})
                                              </p>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium text-green-600">
                                          {formatCurrency(agentAmount.toFixed(2))}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                {records.length > 0 && (
                                  <tfoot className="bg-muted/30 border-t">
                                    <tr>
                                      <td className="px-4 py-3 text-sm font-medium text-right" colSpan={7}>
                                        Total
                                      </td>
                                      <td className="px-4 py-3 text-sm font-semibold text-green-700">
                                        {formatCurrency(totalAgentAmount.toFixed(2))}
                                      </td>
                                    </tr>
                                  </tfoot>
                                )}
                              </table>
                            </div>
                            {records.length === 0 && (
                              <div className="text-center py-8 text-muted-foreground">
                                No commission records found for this agent's clients.
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="split-management" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Split Management</h3>
              <p className="text-sm text-muted-foreground">
                Assign agency/agent and primary/secondary split profiles in one place.
              </p>
            </div>
            <Button variant="outline" onClick={() => setCommissionSettingsOpen(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Manage Split Profiles
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Agency/Agent Split Assignments
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="text-muted-foreground hover:text-foreground transition-colors">
                          <HelpCircle className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-sm">
                        <div className="space-y-2">
                          <p className="font-semibold text-xs">How this works:</p>
                          <ul className="space-y-1 text-xs">
                            <li>• Each <strong>client</strong> has one agency/agent split</li>
                            <li>• That split is set by whoever is the <strong>primary producer</strong> for that client</li>
                            <li>• Assigning a split to an agent updates all clients where they're primary</li>
                            <li>• Secondary producers don't affect client splits (they only share in the agent's portion)</li>
                          </ul>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CardTitle>
                <CardDescription>
                  Assign splits to all clients where the agent is the <strong>primary producer</strong>.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {agencyAgentSplitProfiles.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    No agency/agent split profiles yet. Click “Manage Split Profiles” to create one.
                  </div>
                ) : getProducerGroups.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No producers found.</div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50 border-b">
                          <tr>
                            <th className="text-left px-4 py-3 text-sm font-medium">Agent</th>
                            <th className="text-left px-4 py-3 text-sm font-medium">Primary Clients</th>
                            <th className="text-left px-4 py-3 text-sm font-medium">Secondary Clients</th>
                            <th className="text-left px-4 py-3 text-sm font-medium">Assign Split</th>
                          </tr>
                        </thead>
                      <tbody className="divide-y">
                        {getProducerGroups
                          .filter((producer: any) => producer.primaryClients.length > 0)
                          .map((producer: any) => {
                          const primaryClientNames = producer.primaryClients
                            .map((id: number) => clients.find((c: any) => c.id === id)?.name || clients.find((c: any) => c.id === id)?.companyName || `Client ${id}`);
                          const secondaryClientNames = producer.secondaryClients
                            .map((id: number) => clients.find((c: any) => c.id === id)?.name || clients.find((c: any) => c.id === id)?.companyName || `Client ${id}`);
                          
                          const expanded = producerExpandedState[producer.userId] || false;
                          
                          // Get the current split assigned to the first primary client (they should all have the same split for this producer)
                          const firstPrimaryClient = clients.find((c: any) => c.id === producer.primaryClients[0]);
                          const currentSplitId = firstPrimaryClient?.agencyAgentSplitId?.toString() || "";
                          const selectedSplit = producerSelectedSplits[producer.userId] || currentSplitId;
                          
                          return (
                            <React.Fragment key={producer.userId}>
                              <tr className="hover:bg-muted/50">
                                <td className="px-4 py-3 text-sm font-medium">{producer.name}</td>
                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                  <button
                                    onClick={() => setProducerExpandedState(prev => ({
                                      ...prev,
                                      [producer.userId]: !prev[producer.userId]
                                    }))}
                                    className="flex items-center gap-1 underline decoration-dotted cursor-pointer hover:text-foreground"
                                  >
                                    <span>{producer.primaryClients.length}</span>
                                    <span className="text-xs">({expanded ? 'hide' : 'show'})</span>
                                  </button>
                                </td>
                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                  {producer.secondaryClients.length > 0 ? (
                                    <TooltipProvider delayDuration={200}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="underline decoration-dotted cursor-pointer">
                                            {producer.secondaryClients.length}
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="right" className="max-w-xs">
                                          <div className="space-y-1">
                                            <p className="font-semibold text-xs">Secondary Clients (not affected):</p>
                                            {secondaryClientNames.map((name: string, idx: number) => (
                                              <p key={idx} className="text-xs">{name}</p>
                                            ))}
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : (
                                    <span className="text-muted-foreground/50">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <Select 
                                    value={selectedSplit} 
                                    onValueChange={(value) => {
                                      if (!value) return;
                                      const splitId = parseInt(value);
                                      
                                      // Update state
                                      setProducerSelectedSplits(prev => ({
                                        ...prev,
                                        [producer.userId]: value
                                      }));
                                      
                                      // Apply to all primary clients immediately
                                      producer.primaryClients.forEach((clientId: number) => {
                                        updateClientAgencySplit(clientId, splitId);
                                      });
                                      
                                      toast({
                                        title: "Split Assigned",
                                        description: `Updated ${producer.primaryClients.length} client${producer.primaryClients.length !== 1 ? 's' : ''}`,
                                        variant: "default"
                                      });
                                    }}
                                    disabled={!canManageAgencyCommissions}
                                  >
                                    <SelectTrigger className="w-[240px] h-8">
                                      <SelectValue placeholder="Select split..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {agencyAgentSplitProfiles.map((profile: any) => (
                                        <SelectItem key={profile.id} value={profile.id.toString()}>
                                          {profile.name} ({profile.isMilestoneBased ? "Milestone" : `${profile.agentPercentage}% Agent`})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </td>
                              </tr>
                              {expanded && (
                                <tr>
                                  <td colSpan={4} className="px-4 py-2 bg-muted/30">
                                    <div className="text-xs">
                                      <p className="font-medium mb-2">Clients that will be updated:</p>
                                      <ul className="space-y-1 ml-4">
                                        {primaryClientNames.map((name: string, idx: number) => (
                                          <li key={idx}>• {name}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Primary/Secondary Split Assignments
                </CardTitle>
                <CardDescription>
                  Splits the agent portion between primary and secondary producers (per client).
                </CardDescription>
              </CardHeader>
              <CardContent>
                {primarySecondarySplitProfiles.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    No primary/secondary split profiles yet. Click “Manage Split Profiles” to create one.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b">
                        <tr>
                          <th className="text-left px-4 py-3 text-sm font-medium">Client</th>
                          <th className="text-left px-4 py-3 text-sm font-medium">Primary</th>
                          <th className="text-left px-4 py-3 text-sm font-medium">Secondary</th>
                          <th className="text-left px-4 py-3 text-sm font-medium">Split</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {(() => {
                          const clientGroups: Record<number, any[]> = {};
                          allClientAccountManagers.forEach((manager: any) => {
                            if (!clientGroups[manager.clientId]) clientGroups[manager.clientId] = [];
                            clientGroups[manager.clientId].push(manager);
                          });

                          return Object.entries(clientGroups)
                            .map(([clientIdStr, managers]) => {
                              const clientId = parseInt(clientIdStr);
                              const client = clients.find((c: any) => c.id === clientId);
                              if (!client) return null;

                              const primaryProducer = managers.find(
                                (m: any) => m.role?.toLowerCase().includes('primary') && m.role?.toLowerCase().includes('producer')
                              );
                              const secondaryProducer = managers.find(
                                (m: any) => m.role?.toLowerCase().includes('secondary') && m.role?.toLowerCase().includes('producer')
                              );

                              // Only show if both primary and secondary exist
                              if (!primaryProducer || !secondaryProducer) return null;

                              const primaryEmployee = primaryProducer
                                ? employees.find((emp: any) => emp.id?.toString() === primaryProducer.userId)
                                : null;
                              const secondaryEmployee = secondaryProducer
                                ? employees.find((emp: any) => emp.id?.toString() === secondaryProducer.userId)
                                : null;

                              const primaryName = primaryEmployee
                                ? `${primaryEmployee.firstName} ${primaryEmployee.lastName}`
                                : primaryProducer?.userId || '-';
                              const secondaryName = secondaryEmployee
                                ? `${secondaryEmployee.firstName} ${secondaryEmployee.lastName}`
                                : secondaryProducer?.userId || '-';

                              return (
                                <tr key={clientId} className="hover:bg-muted/50">
                                  <td className="px-4 py-3 text-sm font-medium">{client.name}</td>
                                  <td className="px-4 py-3 text-sm">{primaryName}</td>
                                  <td className="px-4 py-3 text-sm">{secondaryName}</td>
                                  <td className="px-4 py-3 text-sm">
                                    <Select
                                      value={
                                        clientAgentSplitAssignments[clientId]?.toString() ||
                                        primarySecondarySplitProfiles.find(p => p.isDefault)?.id?.toString() ||
                                        ""
                                      }
                                      onValueChange={(value) => updateClientAgentSplit(clientId, parseInt(value))}
                                      disabled={!canManageAgencyCommissions}
                                    >
                                      <SelectTrigger className="w-[200px] h-8">
                                        <SelectValue placeholder="Select split" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {primarySecondarySplitProfiles.map((profile: any) => (
                                          <SelectItem key={profile.id} value={profile.id.toString()}>
                                            {profile.name} ({profile.primaryPercentage}%/{profile.secondaryPercentage}%)
                                            {profile.isDefault ? " (Default)" : ""}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                </tr>
                              );
                            })
                            .filter(Boolean);
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="assignments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Producer Assignments
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                View and manage all currently assigned producers for each client. Assign primary and secondary producers, and (when a secondary is assigned) choose the primary/secondary split applied to the agent portion.
              </p>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Input
                  placeholder="Search companies..."
                  value={assignmentSearchTerm}
                  onChange={(e) => setAssignmentSearchTerm(e.target.value)}
                  className="w-64"
                />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th 
                        className="text-left px-4 py-3 text-sm font-medium cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleAssignmentSort('client')}
                      >
                        Client {getSortIcon('client')}
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-medium">
                        Scheduled Producers
                      </th>
                      <th 
                        className="text-left px-4 py-3 text-sm font-medium cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleAssignmentSort('primaryAgent')}
                      >
                        Primary Agent {getSortIcon('primaryAgent')}
                      </th>
                      <th 
                        className="text-left px-4 py-3 text-sm font-medium cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleAssignmentSort('secondaryAgent')}
                      >
                        Secondary Agent {getSortIcon('secondaryAgent')}
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-medium">Split</th>
                      <th 
                        className="text-left px-4 py-3 text-sm font-medium cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleAssignmentSort('status')}
                      >
                        Status {getSortIcon('status')}
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(() => {
                      const displayEmployeeName = (emp: any) => {
                        const raw = `${emp?.firstName || ""} ${emp?.lastName || ""}`.trim();
                        return raw || emp?.email || `Employee ${emp?.id}`;
                      };

                      // Build rows data first for sorting
                      let rowsData = clients
                        .map((client: any) => {
                          const clientId = Number(client.id);
                          if (!Number.isFinite(clientId)) return null;

                          const today = new Date();
                          today.setHours(0, 0, 0, 0);

                          // Only show currently ACTIVE assignments in the assignments table
                          const managers = allClientAccountManagers.filter((m: any) => 
                            m.clientId === clientId && isAssignmentCurrentlyActive(m)
                          );
                          
                          // Get all producer assignments (not just primary/secondary)
                          const allProducers = managers.filter((m: any) => m.role?.toLowerCase().includes("producer"));

                          const scheduledProducers = allClientAccountManagers
                            .filter((m: any) => {
                              if (m.clientId !== clientId) return false;
                              if (!m.role?.toLowerCase().includes("producer")) return false;
                              if (!m.effectiveDate) return false;
                              const eff = parseDateLocal(m.effectiveDate);
                              if (!eff) return false;
                              const end = m.endDate ? parseDateLocal(m.endDate) : null;
                              if (end && end <= today) return false;
                              return eff > today;
                            })
                            .sort((a: any, b: any) => {
                              const aTime = parseDateLocal(a.effectiveDate)?.getTime() || 0;
                              const bTime = parseDateLocal(b.effectiveDate)?.getTime() || 0;
                              return aTime - bTime;
                            });
                          
                          const primaryProducer = managers.find(
                            (m: any) => m.role?.toLowerCase().includes("primary") && m.role?.toLowerCase().includes("producer")
                          );
                          const secondaryProducer = managers.find(
                            (m: any) => m.role?.toLowerCase().includes("secondary") && m.role?.toLowerCase().includes("producer")
                          );

                          const primaryOptions = producerEmployees.filter(
                            (emp: any) => String(emp.id) !== String(secondaryProducer?.userId || "")
                          );
                          const secondaryOptions = producerEmployees.filter(
                            (emp: any) => String(emp.id) !== String(primaryProducer?.userId || "")
                          );

                          const split = getClientAgentSplit(clientId);
                          const hasProducers = Boolean(primaryProducer || secondaryProducer);

                          const primaryEmployee = primaryProducer
                            ? employees.find((emp: any) => emp.id?.toString() === primaryProducer.userId)
                            : null;
                          const secondaryEmployee = secondaryProducer
                            ? employees.find((emp: any) => emp.id?.toString() === secondaryProducer.userId)
                            : null;

                          const primaryName = primaryEmployee
                            ? `${primaryEmployee.firstName} ${primaryEmployee.lastName}`
                            : primaryProducer?.userId || "-";
                          const secondaryName = secondaryEmployee
                            ? `${secondaryEmployee.firstName} ${secondaryEmployee.lastName}`
                            : secondaryProducer?.userId || "-";

                          return {
                            clientId,
                            client,
                            clientName: client.name,
                            primaryName,
                            secondaryName,
                            primaryProducer,
                            secondaryProducer,
                            allProducers,
                            scheduledProducers,
                            primaryOptions,
                            secondaryOptions,
                            split,
                            hasProducers
                          };
                        })
                        .filter(Boolean) as any[];

                      // Filter by search term
                      if (assignmentSearchTerm.trim()) {
                        const search = assignmentSearchTerm.trim().toLowerCase();
                        rowsData = rowsData.filter((row: any) => row.clientName.toLowerCase().includes(search));
                      }

                      // Sort the rows
                      const sortedRows = [...rowsData].sort((a, b) => {
                        let aValue: string;
                        let bValue: string;

                        switch (assignmentSortColumn) {
                          case 'client':
                            aValue = a.clientName.toLowerCase();
                            bValue = b.clientName.toLowerCase();
                            break;
                          case 'primaryAgent':
                            aValue = a.primaryName.toLowerCase();
                            bValue = b.primaryName.toLowerCase();
                            break;
                          case 'secondaryAgent':
                            aValue = a.secondaryName.toLowerCase();
                            bValue = b.secondaryName.toLowerCase();
                            break;
                          case 'status':
                            aValue = a.hasProducers ? 'active' : 'no agent';
                            bValue = b.hasProducers ? 'active' : 'no agent';
                            break;
                          default:
                            return 0;
                        }

                        const comparison = aValue.localeCompare(bValue);
                        return assignmentSortDirection === 'asc' ? comparison : -comparison;
                      });

                      return sortedRows.map((row) => {
                        const displayEmployeeNameForProducer = (producer: any) => {
                          const emp = employees.find((e: any) => e.id?.toString() === producer?.userId);
                          return emp ? `${emp.firstName} ${emp.lastName}` : producer?.userId || "Unknown";
                        };
                        
                        return (
                        <tr key={row.clientId} className="hover:bg-muted/50">
                          <td className="px-4 py-3 text-sm font-medium">{row.clientName}</td>
                          <td className="px-4 py-3 text-sm">
                            {row.scheduledProducers.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {row.scheduledProducers.map((producer: any) => {
                                  const roleName = producer.role?.toLowerCase().includes("primary")
                                    ? "Primary"
                                    : producer.role?.toLowerCase().includes("secondary")
                                    ? "Secondary"
                                    : "Producer";
                                  const effLabel = producer.effectiveDate ? String(producer.effectiveDate) : "";
                                  return (
                                    <Badge key={producer.id} variant="outline" className="text-xs flex items-center gap-1 pr-1">
                                      <span>
                                        {displayEmployeeNameForProducer(producer)} ({roleName})
                                        {effLabel ? ` • ${effLabel}` : ""}
                                      </span>
                                      {canManageAgencyCommissions && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPendingRemoval({
                                              clientId: row.clientId,
                                              userId: producer.userId,
                                              role: producer.role,
                                              clientName: row.clientName,
                                              producerName: displayEmployeeNameForProducer(producer),
                                            });
                                          }}
                                          className="ml-1 hover:bg-black/10 rounded-full p-0.5"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      )}
                                    </Badge>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-2">
                              <Select
                                value={row.primaryProducer?.userId}
                                onValueChange={(value) => {
                                  setProducerAssignmentData({
                                    clientId: row.clientId,
                                    userId: value,
                                    role: "primary_producer",
                                    clientName: row.clientName
                                  });
                                  setProducerEffectiveDate(getLocalDateString());
                                  setProducerAssignmentDialogOpen(true);
                                }}
                                disabled={!canManageAgencyCommissions}
                              >
                                <SelectTrigger className="w-[220px] h-8">
                                  <SelectValue placeholder="Select primary" />
                                </SelectTrigger>
                                <SelectContent>
                                  {row.primaryOptions.map((emp: any) => (
                                    <SelectItem key={emp.id} value={String(emp.id)}>
                                      {displayEmployeeName(emp)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {row.primaryProducer?.userId && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={!canManageAgencyCommissions}
                                  onClick={() =>
                                    setPendingRemoval({
                                      clientId: row.clientId,
                                      userId: row.primaryProducer.userId,
                                      role: row.primaryProducer.role || "primary_producer",
                                      clientName: row.clientName,
                                      producerName: row.primaryName,
                                    })
                                  }
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-2">
                              <Select
                                value={row.secondaryProducer?.userId}
                                onValueChange={(value) => {
                                  setProducerAssignmentData({
                                    clientId: row.clientId,
                                    userId: value,
                                    role: "secondary_producer",
                                    clientName: row.clientName
                                  });
                                  setProducerEffectiveDate(getLocalDateString());
                                  setProducerAssignmentDialogOpen(true);
                                }}
                                disabled={!canManageAgencyCommissions}
                              >
                                <SelectTrigger className="w-[220px] h-8">
                                  <SelectValue placeholder="Select secondary" />
                                </SelectTrigger>
                                <SelectContent>
                                  {row.secondaryOptions.map((emp: any) => (
                                    <SelectItem key={emp.id} value={String(emp.id)}>
                                      {displayEmployeeName(emp)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {row.secondaryProducer?.userId && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={!canManageAgencyCommissions}
                                  onClick={() =>
                                    setPendingRemoval({
                                      clientId: row.clientId,
                                      userId: row.secondaryProducer.userId,
                                      role: row.secondaryProducer.role || "secondary_producer",
                                      clientName: row.clientName,
                                      producerName: row.secondaryName,
                                    })
                                  }
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {row.secondaryProducer ? (
                              <Select
                                value={
                                  clientAgentSplitAssignments[row.clientId]?.toString() ||
                                  primarySecondarySplitProfiles.find((p) => p.isDefault)?.id?.toString() ||
                                  ""
                                }
                                onValueChange={(value) => updateClientAgentSplit(row.clientId, parseInt(value))}
                                disabled={!canManageAgencyCommissions}
                              >
                                <SelectTrigger className="w-[180px] h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {primarySecondarySplitProfiles.map((profile) => (
                                    <SelectItem key={profile.id} value={profile.id.toString()}>
                                      {profile.name} ({profile.primaryPercentage}%/{profile.secondaryPercentage}%)
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-muted-foreground">100%</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {row.hasProducers ? (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-200">Active</Badge>
                            ) : (
                              <Badge variant="outline">No Agent</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {row.secondaryProducer && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedClientForSplit({
                                    clientId: row.clientId,
                                    clientName: row.clientName,
                                    primaryName: row.primaryName,
                                    secondaryName: row.secondaryName,
                                    ...row.split,
                                  });
                                  setEditClientSplitOpen(true);
                                }}
                              >
                                <Settings className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Edit Client Split Dialog */}
          <Dialog open={editClientSplitOpen} onOpenChange={setEditClientSplitOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Primary/Secondary Split</DialogTitle>
              </DialogHeader>
              {selectedClientForSplit && (
                <div className="space-y-4 py-4">
                  <div>
                    <p className="text-sm font-medium mb-2">Client: {selectedClientForSplit.clientName}</p>
                    <p className="text-sm text-muted-foreground">Primary: {selectedClientForSplit.primaryName}</p>
                    <p className="text-sm text-muted-foreground">Secondary: {selectedClientForSplit.secondaryName}</p>
                  </div>
                  <div>
                    <Label>Select Split Profile</Label>
                    <Select 
                      value={clientAgentSplitAssignments[selectedClientForSplit.clientId]?.toString() || primarySecondarySplitProfiles.find(p => p.isDefault)?.id?.toString()}
                      onValueChange={(value) => {
                        updateClientAgentSplit(selectedClientForSplit.clientId, parseInt(value));
                        setEditClientSplitOpen(false);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a split profile" />
                      </SelectTrigger>
                      <SelectContent>
                        {primarySecondarySplitProfiles.map(profile => (
                          <SelectItem key={profile.id} value={profile.id.toString()}>
                            {profile.name} ({profile.primaryPercentage}% Primary / {profile.secondaryPercentage}% Secondary)
                            {profile.isDefault && " - Default"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-muted-foreground mt-2">
                      This split determines how commissions are divided between primary and secondary agents
                    </p>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Total Commissions (YTD)</p>
                    <p className="text-2xl font-bold">${analyticsData.totalYTD.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground mt-1">All completed and projected</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Avg Monthly Growth</p>
                    <p className="text-2xl font-bold" data-testid="agency-avg-growth">{formatGrowthPct(analyticsData.avgGrowth)}</p>
                    <p className={`text-xs flex items-center gap-1 mt-1 ${(analyticsData.avgGrowth ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(analyticsData.avgGrowth ?? 0) >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {analyticsData.avgGrowth === null ? 'Not enough history' : analyticsData.avgGrowth >= 0 ? 'Growth trend' : 'Decline trend'}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Top Carrier</p>
                    <p className="text-2xl font-bold">{analyticsData.topCarrier}</p>
                    <p className="text-xs text-muted-foreground mt-1">${analyticsData.topCarrierAmount.toLocaleString()} in commissions</p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Top Coverage</p>
                    <p className="text-2xl font-bold">{analyticsData.topCoverage}</p>
                    <p className="text-xs text-muted-foreground mt-1">{analyticsData.topCoveragePercent}% of total revenue</p>
                  </div>
                  <PieChartIcon className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Month-over-Month Variance Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Month-over-Month Commission Trends
              </CardTitle>
              <CardDescription>Track commission growth and variance across all carriers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData.monthlyTrendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip formatter={(value: any, name: string) => name === "commissions" ? [`$${value.toLocaleString()}`, "Commission"] : [`${value}%`, "Variance"]} />
                    <Legend />
                    <Line type="monotone" dataKey="commissions" stroke="#8884d8" name="Monthly Commissions" strokeWidth={2} />
                    <Line type="monotone" dataKey="variance" stroke="#82ca9d" name="% Variance" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Carrier Breakdown and Coverage Type Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Commission by Carrier
                </CardTitle>
                <CardDescription>Compare commission revenue across carriers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.carrierData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="carrier" />
                      <YAxis />
                      <RechartsTooltip formatter={(value: any, name: string) => name === "amount" ? [`$${value.toLocaleString()}`, "Commission"] : [`${value}%`, "Variance"]} />
                      <Legend />
                      <Bar dataKey="amount" fill="#8884d8" name="Total Commission" />
                      <Bar dataKey="variance" fill="#82ca9d" name="% Change" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5" />
                  Commission by Coverage Type
                </CardTitle>
                <CardDescription>Revenue distribution across insurance types</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={analyticsData.coverageTypeData} cx="50%" cy="50%" labelLine={false} label={({ name, percentage }) => `${name} ${percentage}%`} outerRadius={100} fill="#8884d8" dataKey="value">
                        <Cell fill="#8884d8" />
                        <Cell fill="#82ca9d" />
                        <Cell fill="#ffc658" />
                        <Cell fill="#ff7c7c" />
                        <Cell fill="#a4de6c" />
                      </Pie>
                      <RechartsTooltip formatter={(value: any) => `$${value.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Variance Analysis Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Detailed Variance Analysis
              </CardTitle>
              <CardDescription>Month-over-month and year-over-year variance by carrier and coverage type</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium">Carrier</th>
                      <th className="text-left px-4 py-3 text-sm font-medium">Coverage Type</th>
                      <th className="text-right px-4 py-3 text-sm font-medium">Current Month</th>
                      <th className="text-right px-4 py-3 text-sm font-medium">Last Month</th>
                      <th className="text-right px-4 py-3 text-sm font-medium">MoM Variance</th>
                      <th className="text-right px-4 py-3 text-sm font-medium">Last Year</th>
                      <th className="text-right px-4 py-3 text-sm font-medium">YoY Variance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {analyticsData.varianceAnalysis.length > 0 ? (
                      analyticsData.varianceAnalysis.map((item, index) => (
                        <tr key={index} className="hover:bg-muted/50">
                          <td className="px-4 py-3 text-sm font-medium">{item.carrierName}</td>
                          <td className="px-4 py-3 text-sm">{item.coverageType}</td>
                          <td className="px-4 py-3 text-sm text-right">${item.currentMonth.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                          <td className="px-4 py-3 text-sm text-right">${item.lastMonth.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                          <td className="px-4 py-3 text-sm text-right">
                            <span className={`font-medium ${item.momVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {item.momVariance >= 0 ? '+' : ''}{item.momVariance.toFixed(1)}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-right">${item.lastYear.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                          <td className="px-4 py-3 text-sm text-right">
                            <span className={`font-medium ${item.yoyVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {item.yoyVariance >= 0 ? '+' : ''}{item.yoyVariance.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                          No variance data available. Add commission records to see analysis.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Commission Settings Dialog */}
      <Dialog open={commissionSettingsOpen} onOpenChange={setCommissionSettingsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Commission Settings</DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="splits" className="mt-4">
            <TabsList className="grid w-full grid-cols-2 bg-[#eff6fe] dark:bg-gray-800">
              <TabsTrigger value="splits" className="bg-[#eff6fe] dark:bg-gray-700 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Agency/Agent Splits</TabsTrigger>
              <TabsTrigger value="agent-splits" className="bg-[#eff6fe] dark:bg-gray-700 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Primary/Secondary Splits</TabsTrigger>
            </TabsList>

            <TabsContent value="splits" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Agency/Agent Commission Splits</CardTitle>
                      <CardDescription>Define split profiles for commission distribution</CardDescription>
                    </div>
                    <Button onClick={() => setAddSplitProfileOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Split Profile
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Profile Name</TableHead>
                        <TableHead>Agency %</TableHead>
                        <TableHead>Agent %</TableHead>
                        <TableHead>Default</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {agencyAgentSplitProfiles.map((split: any) => (
                        <TableRow key={split.id}>
                          <TableCell className="font-medium">
                            {split.name}
                            {split.isMilestoneBased && <Badge variant="secondary" className="ml-2 text-xs">Milestone</Badge>}
                          </TableCell>
                          <TableCell><Badge variant="outline">{split.isMilestoneBased ? "Varies" : `${split.agencyPercentage}%`}</Badge></TableCell>
                          <TableCell><Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950/20 dark:text-green-400">{split.isMilestoneBased ? "Varies" : `${split.agentPercentage}%`}</Badge></TableCell>
                          <TableCell>{split.isDefault && <Check className="h-4 w-4 text-green-600" />}</TableCell>
                          <TableCell className="text-muted-foreground">{split.description}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={async () => {
                                setEditSplitProfile(split);
                                setSplitFormIsMilestone(split.isMilestoneBased || false);
                                if (split.isMilestoneBased) {
                                  try {
                                    const res = await fetch(`/api/commission-split-profiles/${split.id}/milestones`);
                                    const data = await res.json();
                                    if (Array.isArray(data) && data.length > 0) {
                                      setMilestoneRows(data.map((m: any) => ({
                                        yearNumber: m.yearNumber,
                                        isYearAndBeyond: m.isYearAndBeyond || false,
                                        pct1: m.agencyPercentage || "50",
                                        pct2: m.agentPercentage || "50",
                                      })));
                                    }
                                  } catch {}
                                }
                                setEditSplitOpen(true);
                              }}>
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              {!split.isDefault && (
                                <Button variant="ghost" size="sm" onClick={async () => {
                                  if (confirm(`Delete split profile "${split.name}"?`)) {
                                    try {
                                      await deleteSplitProfile.mutateAsync(split.id);
                                      toast({ title: "Split profile deleted" });
                                    } catch (error: any) {
                                      toast({ title: "Error", description: error.message, variant: "destructive" });
                                    }
                                  }
                                }}>
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="agent-splits" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Primary/Secondary Agent Splits</CardTitle>
                      <CardDescription>Define split profiles for commission distribution between primary and secondary agents on the same client</CardDescription>
                    </div>
                    <Button onClick={() => setAddPrimarySecondarySplitOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Split Profile
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-md">
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      <strong>How it works:</strong> These splits apply to the agent portion from the Agency/Agent split. For example, if a commission is $1,000 and the agent gets 50% ($500), a 60/40 primary/secondary split means the primary agent receives $300 and the secondary receives $200.
                    </p>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Profile Name</TableHead>
                        <TableHead>Primary %</TableHead>
                        <TableHead>Secondary %</TableHead>
                        <TableHead>Default</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {primarySecondarySplitProfiles.map((split: any) => (
                        <TableRow key={split.id}>
                          <TableCell className="font-medium">{split.name}</TableCell>
                          <TableCell><Badge variant="outline" className="bg-blue-50 text-blue-700 dark:bg-blue-950/20 dark:text-blue-400">{split.primaryPercentage}%</Badge></TableCell>
                          <TableCell><Badge variant="outline" className="bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-400">{split.secondaryPercentage}%</Badge></TableCell>
                          <TableCell>
                            {split.isDefault ? (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-200">Default</Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground">{split.description}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  const newPrimary = prompt(`Enter primary percentage for "${split.name}":`, split.primaryPercentage?.toString());
                                  if (newPrimary === null) return;
                                  const newSecondary = prompt(`Enter secondary percentage for "${split.name}":`, split.secondaryPercentage?.toString());
                                  if (newSecondary === null) return;
                                  
                                  const primaryNum = parseFloat(newPrimary);
                                  const secondaryNum = parseFloat(newSecondary);
                                  
                                  if (primaryNum + secondaryNum !== 100) {
                                    toast({
                                      title: "Invalid Split",
                                      description: "Primary and Secondary must add up to 100%",
                                      variant: "destructive"
                                    });
                                    return;
                                  }
                                  
                                  try {
                                    await updateSplitProfile.mutateAsync({
                                      id: split.id,
                                      data: {
                                        primaryPercentage: primaryNum.toString(),
                                        secondaryPercentage: secondaryNum.toString()
                                      }
                                    });
                                    toast({ title: "Split profile updated" });
                                  } catch (error: any) {
                                    toast({ title: "Error", description: error.message, variant: "destructive" });
                                  }
                                }}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              {!split.isDefault && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    if (confirm(`Delete split profile "${split.name}"?`)) {
                                      try {
                                        await deleteSplitProfile.mutateAsync(split.id);
                                        toast({ title: "Split profile deleted" });
                                      } catch (error: any) {
                                        toast({ title: "Error", description: error.message, variant: "destructive" });
                                      }
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Add Split Profile Dialog */}
      <Dialog open={addSplitProfileOpen} onOpenChange={(open) => {
        setAddSplitProfileOpen(open);
        if (!open) {
          setSplitFormName("");
          setSplitFormAgencyPct("");
          setSplitFormAgentPct("");
          setSplitFormDescription("");
          setSplitFormIsDefault(false);
          setSplitFormIsMilestone(false);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Commission Split Profile</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const formEl = e.currentTarget;
            const formData = new FormData(formEl);

            if (!splitFormIsMilestone) {
              const agencyPercentage = parseFloat(formData.get("agencyPercentage") as string);
              const agentPercentage = parseFloat(formData.get("agentPercentage") as string);
              if (agencyPercentage + agentPercentage !== 100) {
                toast({ title: "Invalid Split", description: "Agency and Agent percentages must add up to 100%", variant: "destructive" });
                return;
              }
            } else {
              const beyondIdx = milestoneRows.findIndex((r) => r.isYearAndBeyond);
              const activeRows = beyondIdx !== -1 ? milestoneRows.slice(0, beyondIdx + 1) : milestoneRows;
              for (const row of activeRows) {
                if (Math.abs(parseFloat(row.pct1) + parseFloat(row.pct2) - 100) > 0.01) {
                  toast({ title: "Invalid Split", description: `Year ${row.yearNumber}: percentages must add up to 100%`, variant: "destructive" });
                  return;
                }
              }
            }

            try {
              const profile = await createSplitProfile.mutateAsync({
                name: formData.get("name") as string,
                splitType: "agency_agent",
                agencyPercentage: splitFormIsMilestone ? "50" : formData.get("agencyPercentage") as string,
                agentPercentage: splitFormIsMilestone ? "50" : formData.get("agentPercentage") as string,
                description: formData.get("description") as string || "",
                isDefault: formData.get("isDefault") === "on",
                isMilestoneBased: splitFormIsMilestone,
                isGlobal: true
              });

              if (splitFormIsMilestone && profile?.id) {
                const beyondIdx = milestoneRows.findIndex((r) => r.isYearAndBeyond);
                const activeRows = beyondIdx !== -1 ? milestoneRows.slice(0, beyondIdx + 1) : milestoneRows;
                await saveMilestones.mutateAsync({
                  profileId: profile.id,
                  milestones: activeRows.map((r) => ({
                    yearNumber: r.yearNumber,
                    isYearAndBeyond: r.isYearAndBeyond,
                    agencyPercentage: r.pct1,
                    agentPercentage: r.pct2,
                  })),
                });
              }

              toast({ title: "Split profile created", variant: "success" });
              setAddSplitProfileOpen(false);
              formEl.reset();
            } catch (error: any) {
              toast({ title: "Error", description: error.message, variant: "destructive" });
            }
          }}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Profile Name</Label>
                <Input id="name" name="name" placeholder="e.g. Junior Agent Split" required />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isMilestone"
                  checked={splitFormIsMilestone}
                  onChange={(e) => setSplitFormIsMilestone(e.target.checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="isMilestone">Milestone-based (different splits per policy year)</Label>
              </div>

              {!splitFormIsMilestone ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="agencyPercentage">Agency %</Label>
                    <Input id="agencyPercentage" name="agencyPercentage" type="number" step="1" min="0" max="100" placeholder="50" required />
                  </div>
                  <div>
                    <Label htmlFor="agentPercentage">Agent %</Label>
                    <Input id="agentPercentage" name="agentPercentage" type="number" step="1" min="0" max="100" placeholder="50" required />
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border p-3 space-y-3">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Year-Based Splits</div>
                  {(() => {
                    const beyondIdx = milestoneRows.findIndex((r) => r.isYearAndBeyond);
                    return milestoneRows.map((row, idx) => {
                      const isDisabled = beyondIdx !== -1 && idx > beyondIdx;
                      return (
                        <div key={idx} className={`flex items-center gap-2 ${isDisabled ? "opacity-40 pointer-events-none" : ""}`}>
                          <div className="w-20 text-sm font-medium">
                            Year {row.yearNumber}{row.isYearAndBeyond ? "+" : ""}
                          </div>
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <Input
                              type="number" step="1" min="0" max="100" placeholder="Agency %"
                              value={row.pct1}
                              disabled={isDisabled}
                              onChange={(e) => {
                                const updated = [...milestoneRows];
                                updated[idx] = { ...row, pct1: e.target.value };
                                setMilestoneRows(updated);
                              }}
                            />
                            <Input
                              type="number" step="1" min="0" max="100" placeholder="Agent %"
                              value={row.pct2}
                              disabled={isDisabled}
                              onChange={(e) => {
                                const updated = [...milestoneRows];
                                updated[idx] = { ...row, pct2: e.target.value };
                                setMilestoneRows(updated);
                              }}
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={row.isYearAndBeyond}
                              disabled={isDisabled}
                              onChange={(e) => {
                                const updated = [...milestoneRows];
                                updated[idx] = { ...row, isYearAndBeyond: e.target.checked };
                                setMilestoneRows(updated);
                              }}
                              className="h-3.5 w-3.5"
                              title="And beyond"
                            />
                            <span className="text-xs text-muted-foreground">+</span>
                          </div>
                          {milestoneRows.length > 1 && !isDisabled && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => {
                              setMilestoneRows(milestoneRows.filter((_, i) => i !== idx));
                            }}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      );
                    });
                  })()}
                  {!milestoneRows.some((r) => r.isYearAndBeyond) && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const nextYear = milestoneRows.length > 0 ? Math.max(...milestoneRows.map((r) => r.yearNumber)) + 1 : 1;
                        setMilestoneRows([...milestoneRows, { yearNumber: nextYear, isYearAndBeyond: false, pct1: "50", pct2: "50" }]);
                      }}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add Year
                    </Button>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" name="description" placeholder="Describe when this split should be used" />
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="isDefault" name="isDefault" className="h-4 w-4" />
                <Label htmlFor="isDefault">Set as default split</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button type="button" variant="outline" onClick={() => setAddSplitProfileOpen(false)}>Cancel</Button>
              <Button type="submit">Create Split Profile</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Split Profile Dialog */}
      <Dialog open={editSplitOpen} onOpenChange={(open) => { setEditSplitOpen(open); if (!open) setEditSplitProfile(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Commission Split Profile</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);

            if (!splitFormIsMilestone) {
              const agencyPercentage = parseFloat(formData.get("agencyPercentage") as string) || 0;
              const agentPercentage = parseFloat(formData.get("agentPercentage") as string) || 0;
              const total = Math.round((agencyPercentage + agentPercentage) * 100) / 100;
              if (total !== 100) {
                toast({ title: "Invalid Split", description: `Percentages must add up to 100% (currently ${total}%)`, variant: "destructive" });
                return;
              }
            } else {
              const beyondIdx = milestoneRows.findIndex((r) => r.isYearAndBeyond);
              const activeRows = beyondIdx !== -1 ? milestoneRows.slice(0, beyondIdx + 1) : milestoneRows;
              for (const row of activeRows) {
                if (Math.abs(parseFloat(row.pct1) + parseFloat(row.pct2) - 100) > 0.01) {
                  toast({ title: "Invalid Split", description: `Year ${row.yearNumber}: percentages must add up to 100%`, variant: "destructive" });
                  return;
                }
              }
            }

            try {
              await updateSplitProfile.mutateAsync({
                id: editSplitProfile?.id,
                data: {
                  name: formData.get("name") as string,
                  agencyPercentage: splitFormIsMilestone ? editSplitProfile?.agencyPercentage : String(parseFloat(formData.get("agencyPercentage") as string)),
                  agentPercentage: splitFormIsMilestone ? editSplitProfile?.agentPercentage : String(parseFloat(formData.get("agentPercentage") as string)),
                  description: formData.get("description") as string || "",
                  isDefault: formData.get("isDefault") === "on",
                  isMilestoneBased: splitFormIsMilestone,
                }
              });

              if (splitFormIsMilestone && editSplitProfile?.id) {
                const beyondIdx = milestoneRows.findIndex((r) => r.isYearAndBeyond);
                const activeRows = beyondIdx !== -1 ? milestoneRows.slice(0, beyondIdx + 1) : milestoneRows;
                await saveMilestones.mutateAsync({
                  profileId: editSplitProfile.id,
                  milestones: activeRows.map((r) => ({
                    yearNumber: r.yearNumber,
                    isYearAndBeyond: r.isYearAndBeyond,
                    agencyPercentage: r.pct1,
                    agentPercentage: r.pct2,
                  })),
                });
              }

              toast({ title: "Split profile updated", variant: "success" });
              setEditSplitOpen(false);
              setEditSplitProfile(null);
            } catch (error: any) {
              toast({ title: "Error", description: error.message, variant: "destructive" });
            }
          }}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Profile Name</Label>
                <Input id="edit-name" name="name" defaultValue={editSplitProfile?.name} required />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-isMilestone"
                  checked={splitFormIsMilestone}
                  onChange={(e) => setSplitFormIsMilestone(e.target.checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="edit-isMilestone">Milestone-based (different splits per policy year)</Label>
              </div>

              {!splitFormIsMilestone ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-agencyPct">Agency %</Label>
                    <Input id="edit-agencyPct" name="agencyPercentage" type="number" step="1" min="0" max="100" defaultValue={editSplitProfile?.agencyPercentage} required />
                  </div>
                  <div>
                    <Label htmlFor="edit-agentPct">Agent %</Label>
                    <Input id="edit-agentPct" name="agentPercentage" type="number" step="1" min="0" max="100" defaultValue={editSplitProfile?.agentPercentage} required />
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border p-3 space-y-3">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Year-Based Splits</div>
                  {(() => {
                    const beyondIdx = milestoneRows.findIndex((r) => r.isYearAndBeyond);
                    return milestoneRows.map((row, idx) => {
                      const isDisabled = beyondIdx !== -1 && idx > beyondIdx;
                      return (
                        <div key={idx} className={`flex items-center gap-2 ${isDisabled ? "opacity-40 pointer-events-none" : ""}`}>
                          <div className="w-20 text-sm font-medium">Year {row.yearNumber}{row.isYearAndBeyond ? "+" : ""}</div>
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <Input type="number" step="1" min="0" max="100" placeholder="Agency %" value={row.pct1} disabled={isDisabled}
                              onChange={(e) => { const u = [...milestoneRows]; u[idx] = { ...row, pct1: e.target.value }; setMilestoneRows(u); }} />
                            <Input type="number" step="1" min="0" max="100" placeholder="Agent %" value={row.pct2} disabled={isDisabled}
                              onChange={(e) => { const u = [...milestoneRows]; u[idx] = { ...row, pct2: e.target.value }; setMilestoneRows(u); }} />
                          </div>
                          <div className="flex items-center gap-1">
                            <input type="checkbox" checked={row.isYearAndBeyond} disabled={isDisabled} className="h-3.5 w-3.5" title="And beyond"
                              onChange={(e) => { const u = [...milestoneRows]; u[idx] = { ...row, isYearAndBeyond: e.target.checked }; setMilestoneRows(u); }} />
                            <span className="text-xs text-muted-foreground">+</span>
                          </div>
                          {milestoneRows.length > 1 && !isDisabled && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => setMilestoneRows(milestoneRows.filter((_, i) => i !== idx))}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      );
                    });
                  })()}
                  {!milestoneRows.some((r) => r.isYearAndBeyond) && (
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                      const nextYear = milestoneRows.length > 0 ? Math.max(...milestoneRows.map((r) => r.yearNumber)) + 1 : 1;
                      setMilestoneRows([...milestoneRows, { yearNumber: nextYear, isYearAndBeyond: false, pct1: "50", pct2: "50" }]);
                    }}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add Year
                    </Button>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea id="edit-description" name="description" defaultValue={editSplitProfile?.description || ""} />
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="edit-isDefault" name="isDefault" defaultChecked={editSplitProfile?.isDefault || false} className="h-4 w-4" />
                <Label htmlFor="edit-isDefault">Set as default split profile</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button type="button" variant="outline" onClick={() => { setEditSplitOpen(false); setEditSplitProfile(null); }}>Cancel</Button>
              <Button type="submit">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Primary/Secondary Split Profile Dialog */}
      <Dialog open={addPrimarySecondarySplitOpen} onOpenChange={setAddPrimarySecondarySplitOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Primary/Secondary Split Profile</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const formEl = e.currentTarget;
            const formData = new FormData(formEl);
            const primaryPercentage = parseFloat(formData.get("primaryPercentage") as string);
            const secondaryPercentage = parseFloat(formData.get("secondaryPercentage") as string);

            if (primaryPercentage + secondaryPercentage !== 100) {
              toast({ title: "Invalid Split", description: "Primary and Secondary percentages must add up to 100%", variant: "destructive" });
              return;
            }

            try {
              await createSplitProfile.mutateAsync({
                name: formData.get("name") as string,
                splitType: "primary_secondary",
                primaryPercentage: String(primaryPercentage),
                secondaryPercentage: String(secondaryPercentage),
                description: formData.get("description") as string || "",
                isDefault: formData.get("isDefault") === "on",
                isGlobal: true
              } as any);
              toast({ title: "Split profile created", variant: "success" });
              setAddPrimarySecondarySplitOpen(false);
              formEl.reset();
            } catch (error: any) {
              toast({ title: "Error", description: error.message, variant: "destructive" });
            }
          }}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Profile Name</Label>
                <Input id="name" name="name" placeholder="e.g. 60/40 Primary Heavy" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="primaryPercentage">Primary %</Label>
                  <Input id="primaryPercentage" name="primaryPercentage" type="number" step="1" min="0" max="100" placeholder="60" required />
                </div>
                <div>
                  <Label htmlFor="secondaryPercentage">Secondary %</Label>
                  <Input id="secondaryPercentage" name="secondaryPercentage" type="number" step="1" min="0" max="100" placeholder="40" required />
                </div>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" name="description" placeholder="Describe when this split should be used" />
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="isDefault" name="isDefault" className="h-4 w-4" />
                <Label htmlFor="isDefault">Set as default split</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button type="button" variant="outline" onClick={() => setAddPrimarySecondarySplitOpen(false)}>Cancel</Button>
              <Button type="submit">Create Split Profile</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Producer Removal Dialog */}
      <AlertDialog open={!!pendingRemoval} onOpenChange={(open) => !open && setPendingRemoval(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Producer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{pendingRemoval?.producerName}</strong> from <strong>{pendingRemoval?.clientName}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex justify-end gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (pendingRemoval) {
                  removeClientRepresentativeMutation.mutate({
                    clientId: pendingRemoval.clientId,
                    userId: pendingRemoval.userId,
                    role: pendingRemoval.role,
                    ...(pendingRemoval.actualDelete ? { actualDelete: true, managerId: pendingRemoval.managerId } : {}),
                  });
                }
                setPendingRemoval(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Producer Assignment Effective Date Dialog */}
      <Dialog open={producerAssignmentDialogOpen} onOpenChange={setProducerAssignmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Producer Effective Date</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {producerAssignmentData && (
                <>
                  Assigning producer to <strong>{producerAssignmentData.clientName}</strong><br />
                  Role: <strong>{producerAssignmentData.role === "primary_producer" ? "Primary Producer" : "Secondary Producer"}</strong>
                </>
              )}
            </p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="producerEffectiveDate">Effective Date</Label>
              <p className="text-xs text-muted-foreground mb-2">
                The previous producer will be ended the day before this date.
              </p>
              <Input
                id="producerEffectiveDate"
                type="date"
                value={producerEffectiveDate}
                onChange={(e) => setProducerEffectiveDate(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setProducerAssignmentDialogOpen(false);
                setProducerAssignmentData(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (producerAssignmentData) {
                  upsertClientRepresentativeMutation.mutate({
                    clientId: producerAssignmentData.clientId,
                    userId: producerAssignmentData.userId,
                    role: producerAssignmentData.role,
                    effectiveDate: producerEffectiveDate,
                  });
                  setProducerAssignmentDialogOpen(false);
                  setProducerAssignmentData(null);
                }
              }}
            >
              Assign Producer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

